import React from 'react';
import type { EntryMethodType } from '../../stores/queueFilterStore';
import {
  Typography,
  Stack,
  Chip,
  Tooltip,
  IconButton,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  TextField,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import {
  Medication,
  Info,
  StickyNote2,
  AddComment,
  LocalShipping,
  Description,
  Science,
  HomeOutlined,
  Business,
  LocalHospital,
  ReceiptLong,
  LocalPharmacy,
  Assignment,
  Autorenew,
  Edit as EditIcon,
  Help as HelpIcon,
  Receipt,
  Lock as LockIcon,
  Warning,
  CheckCircle,
  Draw as DrawIcon,
  AcUnit,
  Inventory2Outlined,
  ReportProblem,
  SignalCellularConnectedNoInternet0Bar,
  Loop,
  WarningAmber,
  HomeWork,
  Sms as SmsIcon,
  PlaylistAdd as TaskQueueIcon,
  PlaylistAddCheck as TaskQueuedIcon,
} from '@mui/icons-material';
import BuildIcon from '@mui/icons-material/Build';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { formatDateOnly, formatDateTime, getTodayString, addDays } from '../../utils/timeUtils';
import { getAgingBucketColor } from '../../utils/agingBucketColors';
import { ClickablePatientName } from '../common';
import { isNursingHomeOrder, getNursingHomeGroupName } from '../../utils/nursingHomeOrder';
import {
  relatedOrdersChipLabel,
  relatedOrdersTooltip,
  toRelatedOrders,
} from '../../utils/relatedOrders';
import { NursingHomeDeliveryTypeChip } from './NursingHomeDeliveryTypeChip';
// PRX-48: every pack string comes from here, so a chip, a cell and the Patient
// 360 header line can never word the same value two different ways.
import {
  CHANGE_CHIP_LABEL,
  CHANGE_CHIP_TOOLTIP,
  PACK_CHIP_LABEL,
  formatPackDueLabel,
  formatRidesWith,
  packChipTooltip,
  ridingPackDateForItem,
} from '../../utils/blisterRiding';

import { stickySurface, panelNeutralHover } from '../../theme/gridSurfaces';
import { DELIVERY_STATUS_ROUTE, deliveryStatusTabFor } from '../../utils/deliveryStatusRoute';
import { pickupStageLabelForOrder } from '../../utils/pickupStageLabel';
// Helper function to calculate days until delivery
export const calculateDaysUntilDelivery = (deliveryDateStr: string | null): number | null => {
  if (!deliveryDateStr) return null;

  const todayStr = getTodayString();
  const [todayYear, todayMonth, todayDay] = todayStr.split('-').map(Number);
  const [deliveryYear, deliveryMonth, deliveryDay] = deliveryDateStr.split('-').map(Number);

  const todayDate = Date.UTC(todayYear, todayMonth - 1, todayDay);
  const deliveryDateUTC = Date.UTC(deliveryYear, deliveryMonth - 1, deliveryDay);

  return Math.floor((deliveryDateUTC - todayDate) / (1000 * 60 * 60 * 24));
};

// Helper function to get delivery status
export const getDeliveryStatus = (deliveryDateStr: string | null) => {
  if (!deliveryDateStr) {
    return { label: '', color: 'default' as any, fontWeight: 'normal' };
  }

  const today = getTodayString();
  const tomorrow = addDays(today, 1);
  const daysUntilDelivery = calculateDaysUntilDelivery(deliveryDateStr);

  if (deliveryDateStr === today) {
    return { label: 'TODAY', color: 'error' as any, fontWeight: 'bold' };
  } else if (deliveryDateStr === tomorrow) {
    return { label: 'TOMORROW', color: 'warning' as any, fontWeight: 'bold' };
  } else if (daysUntilDelivery === 2) {
    return { label: 'Day After Tomorrow', color: 'info' as any, fontWeight: 'medium' };
  }

  return { label: '', color: 'default' as any, fontWeight: 'normal' };
};

// Column factory functions
export const createOrderNumberColumn = (): GridColDef => ({
  field: 'orderNumber',
  headerName: 'Order #',
  width: 120,
  renderCell: (params: GridRenderCellParams) => (
    <Typography variant="body2" fontWeight="medium">
      {params.row.orderNumber}
    </Typography>
  ),
});

export const createRxNumberColumn = (): GridColDef => ({
  field: 'rxNumber',
  headerName: 'Rx Number',
  width: 130,
  renderCell: (params: GridRenderCellParams) => (
    <Typography variant="body2" fontWeight="medium">
      {params.row.rxNumber}
    </Typography>
  ),
});

export const createPatientNameColumn = (
  sourceSystem: string,
  fontWeight?: 'normal' | 'medium' | 'bold'
): GridColDef => ({
  field: 'patientName',
  headerName: 'Patient Name',
  minWidth: 150,
  flex: 1,
  renderCell: (params: GridRenderCellParams) => (
    <ClickablePatientName
      patientName={params.row.patientName}
      patientId={params.row.patientId}
      krollGroup={params.row.metadata?.krollGroup}
      sourceSystem={sourceSystem}
      fontWeight={fontWeight}
    />
  ),
});

// Per-drug fulfillment stage filter: when a queueStage is provided and the row
// carries stageRxNumbers (Record<stage, rxNumbers[]> from the backend queue API),
// returns a Set of the rxNumbers that belong at this queue's stage. Returns null
// — meaning "show all drugs" — when the signal is ABSENT (older payloads) OR
// EMPTY for this stage. The empty case matters: an order can appear in a queue
// via its workflow_stage even if no per-drug stage matches (home-stage guarantee
// / lagging data); we must show its drugs rather than render the row drug-less.
export const getStageRxSet = (item: any, queueStage?: string): Set<string> | null => {
  if (!queueStage) return null;
  const rxNumbers = item?.stageRxNumbers?.[queueStage];
  if (!Array.isArray(rxNumbers) || rxNumbers.length === 0) return null;
  return new Set(rxNumbers.map((rx: any) => String(rx)));
};

/**
 * The medications a queue row actually represents at `queueStage`: the order's
 * billing medications minus the ones split off to Troubleshooting, minus
 * zero-fill drugs (whole quantity deferred to a BO- clone), narrowed to the
 * drugs at this queue's stage when the per-drug signal is present.
 *
 * Extracted from the Drug / Prescriber column so a ROW ACTION can act on exactly
 * the drugs the pharmacist can see on that row (GP-1487 — "Send to task queue"),
 * instead of re-deriving the filter chain and drifting from what's displayed.
 * See CLAUDE.md, "One source of truth for shared data".
 */
export const getQueueStageMedications = (item: any, queueStage?: string): any[] => {
  const allMedications = item?.metadata?.billingData?.medications;
  if (!Array.isArray(allMedications)) return [];

  const splitRxNumbers = new Set(
    (item?.metadata?.troubleshootingSplitHistory || []).map((h: any) => String(h.rxNumber))
  );
  const zeroFillRxNumbers = new Set(
    (item?.metadata?.partialFillInfo?.filledQuantities || [])
      .filter((f: any) => Number(f.quantityPackaged) === 0)
      .map((f: any) => String(f.rxNumber))
  );
  const stageRxSet = getStageRxSet(item, queueStage);

  return allMedications.filter((med: any) => {
    const rx = String(med?.rxNumber);
    if (splitRxNumbers.has(rx)) return false;
    if (zeroFillRxNumbers.has(rx)) return false;
    if (stageRxSet && !stageRxSet.has(rx)) return false;
    return true;
  });
};

export const createDrugNameColumn = (showVerificationWarning = false, queueStage?: string): GridColDef => ({
  field: 'drugName',
  headerName: 'Drug Name',
  minWidth: 200,
  flex: 1.5,
  renderCell: (params: GridRenderCellParams) => {
    const item = params.row;
    const allMedications = item.metadata?.billingData?.medications;
    // Per-drug fulfillment stage: when present, show ONLY the drugs at this queue's stage.
    const stageRxSet = getStageRxSet(item, queueStage);
    const medications = stageRxSet
      ? allMedications?.filter((med: any) => stageRxSet.has(String(med.rxNumber)))
      : allMedications;
    const medicationCount = medications?.length || 0;

    return (
      <Stack spacing={0.5} sx={{ py: 1 }}>
        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
          <Medication fontSize="small" color="action" />
          <Typography variant="body2" fontWeight="medium">
            {item.drugName || item.metadata?.drugName || 'Medication'}
          </Typography>
          {item.isRefrigerated && (
            <Tooltip title="Refrigerated - Cold Chain" arrow>
              <AcUnit sx={{ fontSize: '0.9rem', color: 'info.main' }} />
            </Tooltip>
          )}
          {medicationCount > 1 && (
            <Tooltip
              title={
                <Stack spacing={0.5}>
                  <Typography variant="caption" fontWeight="bold">All Medications:</Typography>
                  {medications.map((med: any, idx: number) => (
                    <Typography key={idx} variant="caption">
                      {idx + 1}. {med.medicationName} {med.strength} - Qty: {med.quantity}
                    </Typography>
                  ))}
                </Stack>
              }
              arrow
            >
              <Chip
                label={`+${medicationCount - 1} more`}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ height: 20, fontSize: '0.7rem', cursor: 'help' }}
              />
            </Tooltip>
          )}
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {item.metadata?.strength || item.metadata?.billingData?.strength || 'N/A'} •
          Qty: {item.quantity ?? item.metadata?.quantity ?? 'N/A'}
        </Typography>
        {medicationCount > 1 && (
          <Typography variant="caption" color="primary" fontWeight="medium">
            {medicationCount} medications {showVerificationWarning ? '- verify each UPC' : 'in this order'}
          </Typography>
        )}
      </Stack>
    );
  },
});

// GP-1341: quantity resolution for the UPC / DIN cell.
//
// The Qty shown next to the DIN has to resolve for EVERY row shape. Multi-drug
// rows read the per-Rx quantity out of `billingData.medications`, but single-drug
// rows only read the denormalized order-level `quantity` — which the
// troubleshooting-unified payload does not carry. Result: on a T/S tab that
// reuses these columns (Packaging T/S), every single-Rx row rendered "Qty: —"
// while multi-Rx rows showed real numbers.
//
// Resolve from the medication entry FIRST. It is also the more correct value when
// a multi-drug order has been filtered down to a single drug, because the
// order-level `quantity` is the cross-drug SUM (GP-624).
export const resolveMedicationQuantity = (med: any): number | string | null =>
  med?.quantity ?? med?.dispenseQuantity ?? null;

// A quantity that genuinely isn't on file renders as an explicit marker rather
// than a bare em-dash, so staff can tell "nothing recorded" from "failed to load".
const NoQuantityRecorded: React.FC = () => (
  <Tooltip title="No dispense quantity recorded for this Rx" arrow>
    <Box component="span" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>n/a</Box>
  </Tooltip>
);

export const createUpcDinColumn = (showScanWarning = false, queueStage?: string): GridColDef => ({
  field: 'upcDin',
  headerName: 'UPC / DIN',
  width: 230,
  renderCell: (params: GridRenderCellParams) => {
    const item = params.row;
    const allMedications = item.metadata?.billingData?.medications;
    // Per-drug fulfillment stage: when present, restrict the scan list to the
    // drugs at this queue's stage (null when the signal is absent — show all).
    const stageRxSet = getStageRxSet(item, queueStage);
    const partialFillInfo = item.metadata?.partialFillInfo;
    const isPartialFill = partialFillInfo?.isPartialFill && partialFillInfo?.filledQuantities;
    // Filter: exclude medications split to troubleshooting
    const splitRxNumbers = new Set(
      (item.metadata?.troubleshootingSplitHistory || []).map((h: any) => String(h.rxNumber))
    );
    // Filter: exclude zero-fill drugs (full balance owing — entire qty was deferred to a
    // BO- clone order, nothing was packaged for them, so they should not show up in the
    // Product Verification queue's "SCAN ALL N PRODUCTS" list.
    const zeroFillRxNumbers = new Set(
      (partialFillInfo?.filledQuantities || [])
        .filter((f: any) => Number(f.quantityPackaged) === 0)
        .map((f: any) => String(f.rxNumber))
    );
    const medications = allMedications?.filter((med: any) => {
      const rx = String(med.rxNumber);
      if (splitRxNumbers.has(rx)) return false;
      if (zeroFillRxNumbers.has(rx)) return false;
      if (stageRxSet && !stageRxSet.has(rx)) return false;
      return true;
    });
    const medicationCount = medications?.length || 0;

    // GP-1252: per-drug Product Verification progress. Each drug is committed the
    // moment it's verified (ProductVerificationDialog.persistPartialVerification),
    // so a multi-drug order can sit in this queue partially verified — verify the
    // room-temp meds now, the fridge item later — instead of the old all-or-nothing
    // "SCAN ALL N" flag that hid the drugs a first tech already checked. Only the
    // Product Verification queue surfaces this (showScanWarning); other queues are
    // untouched.
    const verifiedRxSet = new Set<string>(
      showScanWarning
        ? ((item.metadata?.productVerification?.drugVerifications || []) as any[])
            .map((d: any) => String(d?.rxNumber))
        : []
    );
    const verifiedCount = (medications || []).filter((m: any) =>
      verifiedRxSet.has(String(m.rxNumber))
    ).length;
    const hasPartialVerification =
      verifiedCount > 0 && verifiedCount < medicationCount;

    // Helper to get the filled qty for a given rxNumber (or first entry for single-drug)
    const getFilledQty = (rxNumber?: string): { filled: number; prescribed: number } | null => {
      if (!isPartialFill) return null;
      const fills = partialFillInfo.filledQuantities;
      const match = rxNumber
        ? fills.find((f: any) => f.rxNumber === rxNumber)
        : fills[0];
      if (match && match.quantityPackaged < match.quantityPrescribed) {
        return { filled: match.quantityPackaged, prescribed: match.quantityPrescribed };
      }
      return null;
    };

    return (
      <Stack spacing={0.25} sx={{ py: 0.5 }}>
        {medicationCount > 1 ? (
          <>
            {/* Show warning banner for multi-medication orders. GP-1252: once a
                subset is verified, show remaining-only progress instead of the
                binary "SCAN ALL" so a resumed order reads "2 of 3 verified". */}
            {showScanWarning && (
              hasPartialVerification ? (
                <Typography variant="caption" color="success.main" fontWeight="bold" sx={{ fontSize: '0.7rem' }}>
                  ✓ {verifiedCount} OF {medicationCount} VERIFIED — SCAN {medicationCount - verifiedCount} REMAINING
                </Typography>
              ) : (
                <Typography variant="caption" color="warning.main" fontWeight="bold" sx={{ fontSize: '0.7rem' }}>
                  ⚠ SCAN ALL {medicationCount} PRODUCTS
                </Typography>
              )
            )}
            {/* Show all medications with their DIN/UPC and Qty */}
            {medications.map((med: any, idx: number) => {
              const fillInfo = getFilledQty(med.rxNumber);
              // GP-1252: mark drugs already product-verified (green check +
              // strike-through) so a resuming tech sees at a glance what's left.
              const isVerified = verifiedRxSet.has(String(med.rxNumber));
              return (
                <Stack key={idx} direction="row" spacing={0.5} alignItems="center">
                  <Typography variant="caption" color={isVerified ? 'success.main' : 'text.secondary'} sx={{ fontSize: '0.65rem', minWidth: '12px' }}>
                    {isVerified ? '✓' : `${idx + 1}.`}
                  </Typography>
                  <Stack spacing={0}>
                    {/* GP-1252 verified styling + GP-1341 quantity resolution. */}
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: 'medium', color: isVerified ? 'success.main' : undefined, textDecoration: isVerified ? 'line-through' : undefined }}>
                      Rx: {med.rxNumber || 'N/A'} | DIN: {med.din || 'N/A'} | Qty: {fillInfo ? fillInfo.filled : (resolveMedicationQuantity(med) ?? <NoQuantityRecorded />)}
                      {fillInfo && <span style={{ color: '#ed6c02' }}> (of {fillInfo.prescribed})</span>}
                    </Typography>
                    {med.upc && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                        UPC: {med.upc}
                      </Typography>
                    )}
                  </Stack>
                </Stack>
              );
            })}
            {isPartialFill && (
              <Typography variant="caption" color="warning.main" fontWeight="bold" sx={{ fontSize: '0.65rem' }}>
                PARTIAL FILL
              </Typography>
            )}
          </>
        ) : (
          <>
            {(() => {
              const fillInfo = getFilledQty(item.rxNumber);
              // GP-1341: prefer the single remaining medication's own quantity —
              // it is present on payloads that omit the order-level `quantity`
              // (troubleshooting-unified) and is the accurate per-drug value when
              // a multi-drug order was filtered down to one.
              const soleMedication = medicationCount === 1 ? medications?.[0] : undefined;
              const quantity = fillInfo
                ? fillInfo.filled
                : (resolveMedicationQuantity(soleMedication) ?? item.quantity ?? null);
              return (
                <Typography variant="body2" fontWeight="medium" sx={{ fontFamily: 'monospace' }}>
                  DIN: {item.metadata?.din || item.metadata?.billingData?.din || soleMedication?.din || '-'} | Qty: {quantity ?? <NoQuantityRecorded />}
                  {fillInfo && <span style={{ color: '#ed6c02' }}> (of {fillInfo.prescribed})</span>}
                </Typography>
              );
            })()}
            {item.metadata?.billingData?.upc && (
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                UPC: {item.metadata.billingData.upc}
              </Typography>
            )}
            {isPartialFill && (
              <Typography variant="caption" color="warning.main" fontWeight="bold" sx={{ fontSize: '0.65rem' }}>
                PARTIAL FILL
              </Typography>
            )}
          </>
        )}
      </Stack>
    );
  },
});

export const createPrescriberColumn = (): GridColDef => ({
  field: 'prescriber',
  headerName: 'Prescriber Name',
  width: 150,
  renderCell: (params: GridRenderCellParams) => (
    <Typography variant="body2">{params.row.prescriber}</Typography>
  ),
});

export const createDeliveryDateColumn = (): GridColDef => ({
  field: 'deliveryDate',
  headerName: 'Delivery Date (dd/mm/yyyy)',
  width: 180,
  renderCell: (params: GridRenderCellParams) => {
    const item = params.row;
    const deliveryDate = item.shipmentDate;
    const deliveryDateStr = deliveryDate ? deliveryDate.toString().split('T')[0] : null;

    if (!deliveryDateStr) {
      return (
        <Typography variant="body2" color="text.secondary">
          Not scheduled
        </Typography>
      );
    }

    // Calculate aging bucket color
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const delivery = new Date(deliveryDateStr);
    delivery.setHours(0, 0, 0, 0);
    const daysUntil = Math.ceil((delivery.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    const color = getAgingBucketColor(daysUntil);

    const deliveryStatus = getDeliveryStatus(deliveryDateStr);

    return (
      <Stack spacing={0.5} sx={{ py: 1 }}>
        <Typography
          variant="body2"
          fontWeight="bold"
          sx={{ color }}
        >
          {formatDateOnly(deliveryDateStr)}
        </Typography>
        {deliveryStatus.label && (
          <Chip
            label={deliveryStatus.label}
            size="small"
            color={deliveryStatus.color}
            sx={{ fontWeight: 'bold' }}
          />
        )}
        {daysUntil > 2 && (
          <Typography variant="caption" color="text.secondary">
            In {daysUntil} days
          </Typography>
        )}
      </Stack>
    );
  },
});

export const createDeliveryNoteColumn = (
  setSelectedItemForNote: (item: any) => void,
  setOrderNoteDialogOpen: (open: boolean) => void
): GridColDef => ({
  field: 'deliveryNote',
  headerName: 'Delivery Order Note',
  width: 180,
  sortable: false,
  renderCell: (params: GridRenderCellParams) => {
    const item = params.row;
    const note = item.metadata?.internalNote || item.internalNote;

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
        {note ? (
          <>
            <Tooltip title={note}>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: 120,
                  color: 'text.secondary'
                }}
              >
                {note}
              </Typography>
            </Tooltip>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedItemForNote(item);
                setOrderNoteDialogOpen(true);
              }}
            >
              <StickyNote2 fontSize="small" color="primary" />
            </IconButton>
          </>
        ) : (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedItemForNote(item);
              setOrderNoteDialogOpen(true);
            }}
          >
            <AddComment fontSize="small" color="action" />
          </IconButton>
        )}
      </Box>
    );
  },
});

export const createClinicalNoteColumn = (
  setSelectedItemForNote?: (item: any) => void,
  setClinicalNoteDialogOpen?: (open: boolean) => void
): GridColDef => ({
  field: 'clinicalNote',
  headerName: 'Clinical Note',
  width: 150,
  renderCell: (params: GridRenderCellParams) => {
    const item = params.row;

    // If no handlers provided, show read-only version
    if (!setSelectedItemForNote || !setClinicalNoteDialogOpen) {
      return (
        <Typography
          variant="body2"
          noWrap
          sx={{ maxWidth: 150 }}
          title={item.clinicalNote || '-'}
        >
          {item.clinicalNote || '-'}
        </Typography>
      );
    }

    // Editable version with icon button
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%' }}>
        {item.clinicalNote ? (
          <>
            <Tooltip title={item.clinicalNote}>
              <Typography
                variant="caption"
                noWrap
                sx={{
                  flex: 1,
                  maxWidth: 100,
                  color: 'success.main',
                  fontWeight: 'medium'
                }}
              >
                {item.clinicalNote}
              </Typography>
            </Tooltip>
            <Tooltip title="Edit clinical note">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedItemForNote(item);
                  setClinicalNoteDialogOpen(true);
                }}
              >
                <Science fontSize="small" color="success" />
              </IconButton>
            </Tooltip>
          </>
        ) : (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedItemForNote(item);
              setClinicalNoteDialogOpen(true);
            }}
          >
            <AddComment fontSize="small" color="action" />
          </IconButton>
        )}
      </Box>
    );
  },
});

// Entry Method label mapping - 5 consolidated entry methods
export const ENTRY_METHOD_LABELS: Record<string, string> = {
  // New canonical values
  new_start_home_del: 'New Start - Home Del',
  new_start_iv: 'New Start - IV',
  one_time: 'On-Demand',
  subscription_refill: 'Subscription (Refill)',
  iv_refill: 'IV Refill',
  // Legacy backwards-compat (for data not yet migrated)
  patient360_new_start: 'New Start - Home Del',
  patient360_subscription: 'Subscription (Refill)',
  patient360_one_time: 'On-Demand',
  patient360: 'Subscription (Refill)',
  phox_one_time: 'On-Demand',
  phox_todo: 'Subscription (Refill)',
  phox_new_patient: 'New Start - Home Del',
  phox_nexi: 'IV Refill',
  phox_refill: 'Subscription (Refill)',
  clinic_order: 'IV Refill',
  auto_refill: 'Subscription (Refill)',
  manual: 'Subscription (Refill)',
};

// The 5 canonical entry method values for filter dropdowns
export const ENTRY_METHOD_FILTER_OPTIONS: Record<string, string> = {
  new_start_home_del: 'New Start - Home Del',
  new_start_iv: 'New Start - IV',
  one_time: 'On-Demand',
  subscription_refill: 'Subscription (Refill)',
  iv_refill: 'IV Refill',
};

// Get human-readable label for entry method code (FR-008 - returns "Unknown" for null/unrecognized)
export const getEntryMethodLabel = (code?: string | null): string => {
  if (!code) return 'Unknown';
  return ENTRY_METHOD_LABELS[code] || 'Unknown';
};

// Entry Method column factory (FR-002 through FR-005, FR-010)
export const createEntryMethodColumn = (): GridColDef => ({
  field: 'sourceType',
  headerName: 'Entry Method',
  width: 160,
  renderCell: (params: GridRenderCellParams) => {
    const label = getEntryMethodLabel(params.row.sourceType);
    const isNewStart = params.row.sourceType === 'new_start_home_del' || params.row.sourceType === 'new_start_iv';
    return (
      <Typography
        variant="body2"
        noWrap
        sx={{
          fontWeight: isNewStart ? 'bold' : 'normal',
          color: isNewStart ? 'warning.main' : 'text.primary'
        }}
      >
        {label}
      </Typography>
    );
  },
});

/**
 * Catch-up indicator column (shared). Renders a "Catch-up" chip when the order has
 * a drug lagging behind the rest of the order (`hasCatchUpItems`, computed backend-
 * side by getLaggingItems — the same source the assign/ship gate reads, so the badge
 * and the gate never disagree). Single source of truth so every queue shows it
 * identically. Originally inline in PackagingQueue; extracted here so Ready to Ship,
 * Staging, Schedule Delivery, Delivery Status and All Orders can reuse it.
 */
export const createCatchUpColumn = (
  // Optional queue-specific presentation (GP-1157): Ready to Ship renders this
  // as a red per-drug "NOT READY" flag naming each drug and where it is,
  // because there the flag means "cannot be assigned to a bag". Other queues
  // keep the generic amber "Catch-up" chip.
  opts?: {
    label?: string;
    color?: 'warning' | 'error';
    /** Return the tooltip for this row; null/undefined falls back to the generic text. */
    getTooltip?: (row: any) => string | null | undefined;
    /** Extra flag predicate beyond hasCatchUpItems (e.g. per-drug stage data). */
    isFlagged?: (row: any) => boolean;
  },
): GridColDef => ({
  field: 'hasCatchUpItems',
  headerName: '',
  width: 64,
  sortable: false,
  align: 'center',
  headerAlign: 'center',
  renderCell: (params: GridRenderCellParams) => {
    const flagged = params.row.hasCatchUpItems || (opts?.isFlagged?.(params.row) ?? false);
    if (!flagged) return null;
    const tooltip =
      opts?.getTooltip?.(params.row) ||
      'Catch-up drug — a newly-added or held drug is still working its way up the pipeline behind the rest of this order. The order will wait for it before it can ship. Open the order to see which drug.';
    return (
      <Tooltip title={tooltip}>
        <Chip
          label={opts?.label ?? 'Catch-up'}
          size="small"
          color={opts?.color ?? 'warning'}
          variant="filled"
          sx={{ fontWeight: 600, fontSize: '0.7rem', height: 20 }}
        />
      </Tooltip>
    );
  },
});

export const createActionsColumn = (
  handleItemSelect: (item: any) => void,
  openTroubleshootingDialog: (item: any) => void,
  showOrderDetails: boolean,
  setSelectedOrderId: (id: string) => void,
  setOrderDetailsDialogOpen: (open: boolean) => void
): GridColDef => ({
  field: 'actions',
  headerName: 'Actions',
  width: 150,
  sortable: false,
  cellClassName: 'actions-column',
  headerClassName: 'actions-column',
  renderCell: (params: GridRenderCellParams) => {
    const item = params.row;
    const deliveryDate = item.shipmentDate;
    const deliveryDateStr = deliveryDate ? deliveryDate.toString().split('T')[0] : null;
    const today = getTodayString();
    const isToday = deliveryDateStr === today;

    return (
      <Stack direction="row" spacing={0.5} justifyContent="center">
        <Tooltip title="Package">
          <IconButton
            color="primary"
            onClick={(e) => {
              e.stopPropagation();
              handleItemSelect(item);
            }}
            sx={{
              ...(isToday && {
                animation: 'pulse 2s infinite',
                '@keyframes pulse': {
                  '0%': {
                    boxShadow: '0 0 0 0 rgba(25, 118, 210, 0.7)',
                  },
                  '70%': {
                    boxShadow: '0 0 0 10px rgba(25, 118, 210, 0)',
                  },
                  '100%': {
                    boxShadow: '0 0 0 0 rgba(25, 118, 210, 0)',
                  },
                },
              }),
            }}
          >
            <LocalShipping fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Send to Troubleshooting">
          <IconButton
            color="warning"
            onClick={(e) => {
              e.stopPropagation();
              openTroubleshootingDialog(item);
            }}
          >
            <BuildIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {showOrderDetails && (
          <Tooltip title="View Details">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedOrderId(item.id);
                setOrderDetailsDialogOpen(true);
              }}
            >
              <Description fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    );
  },
});

/**
 * PRX-44: the entry methods a pharmacy can filter by. The two IV ones belong to
 * the Clinic / IV module and come out when it is off. Exported so the rule can
 * be asserted without a render harness.
 */
export const IV_ENTRY_METHOD_FILTER_CODES: readonly string[] = ['new_start_iv', 'iv_refill'];

export const entryMethodFilterOptions = (
  clinicModuleEnabled: boolean,
): [string, string][] =>
  Object.entries(ENTRY_METHOD_FILTER_OPTIONS).filter(
    ([code]) => clinicModuleEnabled || !IV_ENTRY_METHOD_FILTER_CODES.includes(code),
  );

// Entry Method Filter Component (FR-009)
interface EntryMethodFilterProps {
  value: EntryMethodType;
  onChange: (value: EntryMethodType) => void;
  size?: 'small' | 'medium';
  disabled?: boolean;
  /** PRX-44: false drops the two IV entry methods. Default true — every pharmacy that has the module. */
  clinicModuleEnabled?: boolean;
}

export const EntryMethodFilter: React.FC<EntryMethodFilterProps> = ({
  value,
  onChange,
  size = 'small',
  disabled = false,
  clinicModuleEnabled = true,
}) => {
  const handleChange = (event: SelectChangeEvent<string>) => {
    onChange(event.target.value as EntryMethodType);
  };

  return (
    <FormControl size={size} sx={{ minWidth: 150 }} disabled={disabled}>
      <InputLabel>Entry Method</InputLabel>
      <Select
        value={value}
        label="Entry Method"
        onChange={handleChange}
      >
        <MenuItem value="all">All Entry Methods</MenuItem>
        {/* A filtered array, not a wrapper component: MUI Select clones its
            direct children to inject the click handler, so a gate around a
            MenuItem would leave the option unselectable. */}
        {entryMethodFilterOptions(clinicModuleEnabled).map(([code, label]) => (
          <MenuItem key={code} value={code}>
            {label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

// ============ Descartes Shipment Type Filter ============
// Filters by whether the order is a CustomerDelivery or CustomerPickup

export const SHIPMENT_TYPE_OPTIONS = [
  { value: 'all', label: 'All Shipment Types' },
  { value: 'CustomerDelivery', label: 'CustomerDelivery' },
  { value: 'CustomerPickup', label: 'CustomerPickup' },
];

interface ShipmentTypeFilterProps {
  value: string;
  onChange: (value: string) => void;
  size?: 'small' | 'medium';
  disabled?: boolean;
}

export const ShipmentTypeFilter: React.FC<ShipmentTypeFilterProps> = ({
  value,
  onChange,
  size = 'small',
  disabled = false,
}) => {
  const handleChange = (event: SelectChangeEvent<string>) => {
    onChange(event.target.value);
  };

  return (
    <FormControl size={size} sx={{ minWidth: 160 }} disabled={disabled}>
      <InputLabel>Shipment Type</InputLabel>
      <Select
        value={value}
        label="Shipment Type"
        onChange={handleChange}
      >
        {SHIPMENT_TYPE_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

// Shipment Type column factory
export const createShipmentTypeColumn = (): GridColDef => ({
  field: 'descartesShipmentType',
  headerName: 'Shipment Type',
  width: 140,
  renderCell: (params: GridRenderCellParams) => {
    const value = params.row.descartesShipmentType;
    const isPickup = value === 'CustomerPickup';
    return (
      <Typography
        variant="body2"
        sx={{
          color: isPickup ? 'warning.main' : 'text.primary',
          fontWeight: isPickup ? 'bold' : 'normal',
        }}
      >
        {value || '—'}
      </Typography>
    );
  },
});

// ============ Descartes Customer Type Filter ============
// Filters by whether the order is a Pt_Home or Infusion_Clinic (or other clinic types)

export const CUSTOMER_TYPE_OPTIONS = [
  { value: 'all', label: 'All Customer Types' },
  { value: 'Pt_Home', label: 'Pt_Home' },
  { value: 'Infusion_Clinic', label: 'Infusion_Clinic' },
];

/**
 * PRX-44: the customer types a pharmacy can filter by. Infusion_Clinic belongs
 * to the Clinic / IV module and comes out when it is off. Exported so the rule
 * can be asserted without a render harness.
 */
export const CLINIC_CUSTOMER_TYPE_VALUES: readonly string[] = ['Infusion_Clinic'];

export const customerTypeFilterOptions = (
  clinicModuleEnabled: boolean,
): { value: string; label: string }[] =>
  CUSTOMER_TYPE_OPTIONS.filter(
    (option) => clinicModuleEnabled || !CLINIC_CUSTOMER_TYPE_VALUES.includes(option.value),
  );

interface CustomerTypeFilterProps {
  value: string;
  onChange: (value: string) => void;
  size?: 'small' | 'medium';
  disabled?: boolean;
  /** PRX-44: false drops the Infusion_Clinic option. Default true — every pharmacy that has the module. */
  clinicModuleEnabled?: boolean;
}

export const CustomerTypeFilter: React.FC<CustomerTypeFilterProps> = ({
  value,
  onChange,
  size = 'small',
  disabled = false,
  clinicModuleEnabled = true,
}) => {
  const handleChange = (event: SelectChangeEvent<string>) => {
    onChange(event.target.value);
  };

  return (
    <FormControl size={size} sx={{ minWidth: 145 }} disabled={disabled}>
      <InputLabel>Customer Type</InputLabel>
      <Select
        value={value}
        label="Customer Type"
        onChange={handleChange}
      >
        {/* A filtered array, not a wrapper component: MUI Select clones its
            direct children to inject the click handler, so a gate around a
            MenuItem would leave the option unselectable. */}
        {customerTypeFilterOptions(clinicModuleEnabled).map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

// Customer Type column factory
export const createCustomerTypeColumn = (): GridColDef => ({
  field: 'descartesCustomerType',
  headerName: 'Customer Type',
  width: 140,
  renderCell: (params: GridRenderCellParams) => {
    const value = params.row.descartesCustomerType;
    const isClinic = value === 'Infusion_Clinic' || (value && value !== 'Pt_Home');
    return (
      <Typography
        variant="body2"
        sx={{
          color: isClinic ? 'info.main' : 'text.primary',
          fontWeight: isClinic ? 'bold' : 'normal',
        }}
      >
        {value || '—'}
      </Typography>
    );
  },
});

// Clinic Name column factory - shows clinic name for clinic orders, "—" for home deliveries
export const createClinicNameColumn = (): GridColDef => ({
  field: 'clinicName',
  headerName: 'Clinic Name',
  width: 160,
  renderCell: (params: GridRenderCellParams) => {
    // Check multiple possible locations for clinic name
    const clinicName = params.row.clinicName ||
                       params.row.metadata?.clinicName ||
                       params.row.metadata?.nexiData?.clinicName ||
                       params.row.metadata?.billingData?.clinicName;
    return (
      <Typography
        variant="body2"
        sx={{
          color: clinicName ? 'info.main' : 'text.secondary',
          fontWeight: clinicName ? 'medium' : 'normal',
        }}
      >
        {clinicName || '—'}
      </Typography>
    );
  },
});

/**
 * PRX-44: the clinic column set, dropped from a grid when the pharmacy's
 * Clinic / IV module is off. Any future clinic-only column joins this set and
 * every grid that already filters through the helper drops it for free.
 */
const CLINIC_COLUMN_FIELDS = new Set(['clinicName']);

export const withoutClinicColumns = (columns: GridColDef[], enabled: boolean): GridColDef[] =>
  enabled ? columns : columns.filter((c) => !CLINIC_COLUMN_FIELDS.has(c.field));

// ============ COMPACT COLUMN FACTORIES FOR CLINICAL VERIFICATION ============
// These columns combine multiple data fields to reduce horizontal space

// Entry method icon and color mapping
export const getEntryMethodIcon = (sourceType?: string | null): { icon: string; color: string; label: string } => {
  switch (sourceType) {
    // New canonical values
    case 'new_start_home_del':
    case 'patient360_new_start':
    case 'phox_new_patient':
      return { icon: 'ReceiptLong', color: 'warning.main', label: getEntryMethodLabel(sourceType) };
    case 'new_start_iv':
      return { icon: 'LocalHospital', color: 'warning.dark', label: 'New Start - IV' };
    case 'one_time':
    case 'patient360_one_time':
    case 'phox_one_time':
      return { icon: 'Receipt', color: 'primary.light', label: getEntryMethodLabel(sourceType) };
    case 'subscription_refill':
    case 'patient360_subscription':
    case 'patient360':
    case 'phox_todo':
    case 'phox_refill':
    case 'auto_refill':
    case 'manual':
      return { icon: 'Assignment', color: 'primary.main', label: getEntryMethodLabel(sourceType) };
    case 'iv_refill':
    case 'phox_nexi':
    case 'clinic_order':
      return { icon: 'LocalHospital', color: 'info.main', label: getEntryMethodLabel(sourceType) };
    default:
      return { icon: 'Help', color: 'text.disabled', label: 'Unknown' };
  }
};

// Helper component to render entry method icon
const EntryMethodIconComponent: React.FC<{ sourceType?: string | null }> = ({ sourceType }) => {
  const config = getEntryMethodIcon(sourceType);
  const iconProps = { fontSize: 'small' as const, sx: { color: config.color } };

  switch (config.icon) {
    case 'ReceiptLong': return <ReceiptLong {...iconProps} />;
    case 'Assignment': return <Assignment {...iconProps} />;
    case 'Receipt': return <Receipt {...iconProps} />;
    case 'LocalPharmacy': return <LocalPharmacy {...iconProps} />;
    case 'LocalHospital': return <LocalHospital {...iconProps} />;
    case 'Autorenew': return <Autorenew {...iconProps} />;
    case 'Edit': return <EditIcon {...iconProps} />;
    default: return <HelpIcon {...iconProps} />;
  }
};

// Column 1: Order Info (Order # + Entry Method) - 140px
export const createCompactOrderInfoColumn = (): GridColDef => ({
  field: 'orderNumber',
  headerName: 'Order / Source',
  width: 140,
  renderCell: (params: GridRenderCellParams) => {
    const entryConfig = getEntryMethodIcon(params.row.sourceType);
    const orderNumber = params.row.orderNumber || params.row.id?.slice(0, 8);
    const isTsSplitOrder = orderNumber?.startsWith('TS-');
    const tsSplitHistory = params.row.metadata?.troubleshootingSplitHistory || [];
    const hasTsSplitHistory = tsSplitHistory.length > 0;
    // GP-1428: other LIVE orders for this patient on this delivery date. Only the
    // Packaging queue payload carries the field today (see QueueService's
    // `includeRelatedOrders`), so the chip appears exactly where the bug bites —
    // the packer deciding whether to build a second bag.
    const relatedOrders = toRelatedOrders(params.row.relatedOrders);

    return (
      <Stack spacing={0.25} sx={{ py: 0.5 }}>
        <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.8rem' }}>
          {orderNumber}
        </Typography>
        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
          <Tooltip title={getEntryMethodLabel(params.row.sourceType)}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <EntryMethodIconComponent sourceType={params.row.sourceType} />
            </Box>
          </Tooltip>
          <Typography
            variant="caption"
            sx={{
              color: entryConfig.color,
              fontWeight: (params.row.sourceType === 'new_start_home_del' || params.row.sourceType === 'new_start_iv') ? 'bold' : 'medium',
              fontSize: '0.7rem',
            }}
          >
            {entryConfig.label}
          </Typography>
          {isTsSplitOrder && (
            <Tooltip title="This order was split from a multi-drug order for troubleshooting" arrow>
              <Chip
                icon={<BuildIcon sx={{ fontSize: '0.65rem' }} />}
                label="TS Split"
                size="small"
                color="warning"
                variant="outlined"
                sx={{ height: 18, fontSize: '0.6rem', fontWeight: 'bold' }}
              />
            </Tooltip>
          )}
          {hasTsSplitHistory && (() => {
            const drugNames = tsSplitHistory.map((h: any) => h.medicationName || h.rxNumber).join(', ');
            return (
              <Tooltip
                title={`${tsSplitHistory.length} drug(s) in troubleshooting: ${drugNames} — check before shipping`}
                arrow
              >
                <Chip
                  icon={<BuildIcon sx={{ fontSize: '0.65rem' }} />}
                  label={`TS: ${tsSplitHistory.length} pending`}
                  size="small"
                  color="warning"
                  sx={{ height: 18, fontSize: '0.6rem', fontWeight: 'bold' }}
                />
              </Tooltip>
            );
          })()}
          {relatedOrders.length > 0 && (
            <Tooltip title={<Box sx={{ whiteSpace: 'pre-line' }}>{relatedOrdersTooltip(relatedOrders)}</Box>} arrow>
              <Chip
                icon={<Inventory2Outlined sx={{ fontSize: '0.65rem' }} />}
                label={relatedOrdersChipLabel(relatedOrders)}
                size="small"
                color="error"
                aria-label={`${relatedOrders.length} related order${relatedOrders.length === 1 ? '' : 's'} for this patient on this delivery date`}
                sx={{ height: 18, fontSize: '0.6rem', fontWeight: 'bold' }}
              />
            </Tooltip>
          )}
        </Stack>
      </Stack>
    );
  },
});

// Column 2: Customer/Clinic (Customer Type + Clinic Name) - 130px
// NOTE: sortable: false because backend doesn't support sorting by descartesCustomerType
// PRX-44: this column mixes the customer type with the clinic name, so it is not
// dropped by withoutClinicColumns. With the Clinic / IV module off it keeps the
// customer type (and the nursing-home branch) and loses only the clinic half.
export const createCompactCustomerClinicColumn = (
  { clinicModuleEnabled = true }: { clinicModuleEnabled?: boolean } = {}
): GridColDef => ({
  field: 'descartesCustomerType',
  headerName: clinicModuleEnabled ? 'Type / Clinic' : 'Type',
  width: 130,
  sortable: false,
  renderCell: (params: GridRenderCellParams) => {
    const customerType = params.row.descartesCustomerType;
    const isClinic = customerType === 'Infusion_Clinic' || (customerType && customerType !== 'Pt_Home');
    const clinicName = params.row.clinicName ||
                       params.row.metadata?.clinicName ||
                       params.row.metadata?.nexiData?.clinicName ||
                       params.row.metadata?.billingData?.clinicName;

    // PRX-48: a non-null pack date means this order IS the pack. The chip sits
    // in the slot the nursing-home chip uses; `entryMethod` gains no sixth
    // value, because it answers how an order entered the system and a pack can
    // enter as a refill or a one-time alike.
    //
    // Both chips render AFTER whichever branch produced the type content, so
    // nothing existing moves and a row carrying neither field renders exactly
    // what it renders today.
    const packDate = params.row.blisterPackDate as string | null | undefined;
    const changePending = params.row.blisterChangePending === true;
    const blisterChips = (packDate || changePending) ? (
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.25 }}>
        {packDate && (
          <Tooltip title={packChipTooltip(packDate, params.row.blisterCycleDays ?? null)}>
            <Chip size="small" variant="outlined" label={PACK_CHIP_LABEL} sx={{ height: 18, fontSize: '0.65rem' }} />
          </Tooltip>
        )}
        {changePending && (
          <Tooltip title={CHANGE_CHIP_TOOLTIP}>
            <Chip size="small" color="warning" label={CHANGE_CHIP_LABEL} sx={{ height: 18, fontSize: '0.65rem' }} />
          </Tooltip>
        )}
      </Stack>
    ) : null;

    // GP-1314: the "Home" below means descartesCustomerType === 'Pt_Home' (a
    // patient's own residence) and has nothing to do with nursing homes — an NH
    // order used to render as a plain "Home" with the destination invisible.
    // The home's name comes from the order's Kroll group stamp; when the stamp
    // is missing we show the mode with NO name rather than guessing one.
    if (isNursingHomeOrder(params.row)) {
      const nhGroupName = getNursingHomeGroupName(params.row);
      return (
        <Stack spacing={0.25} sx={{ py: 0.5 }}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Tooltip title={nhGroupName ? `Nursing home: ${nhGroupName}` : 'Nursing home drop-off'}>
              <HomeWork fontSize="small" sx={{ color: 'secondary.main' }} />
            </Tooltip>
            <Typography variant="caption" sx={{ color: 'secondary.main', fontWeight: 'medium' }}>
              Nursing Home
            </Typography>
          </Stack>
          {nhGroupName && (
            <Tooltip title={nhGroupName}>
              <Typography
                variant="caption"
                sx={{
                  color: 'secondary.main',
                  fontWeight: 'medium',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: 110,
                  fontSize: '0.7rem',
                }}
              >
                {nhGroupName}
              </Typography>
            </Tooltip>
          )}
          {blisterChips}
        </Stack>
      );
    }

    return (
      <Stack spacing={0.25} sx={{ py: 0.5 }}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          {isClinic ? (
            <Tooltip title="Clinic Delivery">
              <Business fontSize="small" sx={{ color: 'info.main' }} />
            </Tooltip>
          ) : (
            <Tooltip title="Home Delivery">
              <HomeOutlined fontSize="small" sx={{ color: 'text.secondary' }} />
            </Tooltip>
          )}
          <Typography variant="caption" sx={{ color: isClinic ? 'info.main' : 'text.secondary', fontWeight: isClinic ? 'medium' : 'normal' }}>
            {isClinic ? 'Clinic' : 'Home'}
          </Typography>
        </Stack>
        {clinicModuleEnabled && isClinic && clinicName && (
          <Tooltip title={clinicName}>
            <Typography
              variant="caption"
              sx={{
                color: 'info.main',
                fontWeight: 'medium',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: 110,
                fontSize: '0.7rem',
              }}
            >
              {clinicName}
            </Typography>
          </Tooltip>
        )}
        {blisterChips}
      </Stack>
    );
  },
});

/**
 * How many medications this row actually puts IN the pack.
 *
 * Zero when the row shipped no medication list — honest for a projection that
 * did not carry one, and never a guessed count staff would plan a card around.
 */
function inPackCount(row: any): number {
  const medications = Array.isArray(row?.medications) ? row.medications : [];
  return medications.filter(
    (m: any) => m?.status !== 'removed' && m?.blister?.placement === 'pack',
  ).length;
}

/**
 * PRX-48 — is the Pack due column worth a column of width on this page?
 *
 * `QueueView` renders the billing To-Be-Billed grid AND both troubleshooting
 * tabs, so an unconditional column would appear on every GreenShield queue in
 * the product. Gated on the CURRENT PAGE's rows: a store with no blister
 * patients sees no new column and no DOM change.
 *
 * A row carrying a pack date counts even when its `isBlisterPatient` stamp was
 * never resolved — that row IS the pack, and hiding the column would hide the
 * one row it exists for.
 */
export function shouldShowPackDueColumn(rows: any[] | null | undefined): boolean {
  if (!Array.isArray(rows)) return false;
  return rows.some((row) => row?.isBlisterPatient === true || !!row?.blisterPackDate);
}

/**
 * PRX-48 — "Pack due": what the pack is, and what travels with it.
 *
 * @mui/x-data-grid v8 COMMUNITY has no row grouping — that is a Premium
 * feature and a licence is a dependency change this work does not make. The
 * group header from spec section 7 is therefore rendered PER ROW in this
 * column, and a blister patient's rows still sit together because they already
 * sort by the same date. The strings are unchanged.
 *
 * A row that is not a blister row renders nothing, so a GreenShield queue that
 * mounts this column gains an empty cell and no other change.
 */
export const createBlisterPackDueColumn = (): GridColDef => ({
  field: 'blisterPackDue',
  headerName: 'Pack due',
  width: 200,
  sortable: true,
  // Sort a pack row by its pack date and everything else by its own due date,
  // so the packs cluster instead of scattering through the queue.
  valueGetter: (_v: any, row: any) => row?.blisterPackDate ?? row?.nextFollowUpDate ?? '',
  renderCell: (params: GridRenderCellParams) => {
    const packDate = params.row.blisterPackDate as string | null | undefined;
    if (packDate) {
      return <Typography variant="caption">{formatPackDueLabel(packDate, inPackCount(params.row))}</Typography>;
    }
    if (params.row.isBlisterPatient !== true) return null;
    // The row's OWN due date against the PATIENT's schedule. Both queues send
    // `nextFollowUpDate` as a plain 'YYYY-MM-DD' day — a DATE column on the
    // billing row, normalised at the projection on the order row — so it is
    // compared as a string and never passed to `new Date`, which would read a
    // bare day as the day before west of London.
    const ownDate = params.row.nextFollowUpDate as string | null | undefined;
    const packDateForPatient = params.row.patientBlisterNextPackDate as string | null | undefined;
    // WITHOUT ALL THREE INPUTS THIS CELL SAYS NOTHING. "Ships on its own date"
    // is a conclusion — we looked, and no pack sits in this item's window — so
    // producing it from a missing schedule would be a definite claim built from
    // absent data. An empty cell reads as "not known here", which is true.
    if (!ownDate || !packDateForPatient || !params.row.blisterCycleDays) return null;
    const rides = ridingPackDateForItem(ownDate, packDateForPatient, params.row.blisterCycleDays);
    return <Typography variant="caption" color="text.secondary">{formatRidesWith(rides)}</Typography>;
  },
});

// Column 3: Patient/Rx (Patient Name + Rx Number) - fixed width 130px
// IMPORTANT: Shows ALL Rx numbers for multi-Rx orders per user requirement
// Checks: 1) items array, 2) metadata.billingData.medications array, 3) single rxNumber fallback
// NOTE: Kept narrow per user requirement - drug/qty column gets more space
export const createCompactPatientRxColumn = (
  handlePatientClick: (row: any) => void
): GridColDef => ({
  field: 'patientName',
  headerName: 'Patient / Rx',
  width: 130,
  renderCell: (params: GridRenderCellParams) => {
    // Collect all Rx numbers from multiple sources, excluding TS-split medications
    // and zero-fill drugs (full balance owing — entire qty deferred to a BO- clone order).
    const splitRxNumbers = new Set(
      (params.row.metadata?.troubleshootingSplitHistory || []).map((h: any) => String(h.rxNumber))
    );
    const zeroFillRxNumbers = new Set(
      (params.row.metadata?.partialFillInfo?.filledQuantities || [])
        .filter((f: any) => Number(f.quantityPackaged) === 0)
        .map((f: any) => String(f.rxNumber))
    );
    const orderRxSet = new Set(
      (params.row.rxNumber || '').split(',').map((rx: string) => rx.trim()).filter(Boolean)
    );
    // Per-drug fulfillment stage: a drug lives in exactly ONE stage at a time. When
    // this row carries the queue's per-stage Rx breakdown (stageRxNumbers + queueStage,
    // set on the CV / Packaging / Product Verification queues), show ONLY the Rx
    // numbers at THIS queue's stage. Without it, a split order that legitimately
    // appears in multiple queues (e.g. a catch-up drug in Packaging while the rest of
    // the order is in Product Verification) would list the SAME Rx in every queue's
    // Patient/Rx column — UAT 2026-05-28 (Cahill): "drug showing up in multiple places".
    // getStageRxSet returns null when the signal is absent (other queues / older
    // payloads), so those views keep showing every Rx — no behaviour change there.
    const stageRxSet = getStageRxSet(params.row, params.row.queueStage);
    const isActiveMed = (rx: string): boolean => {
      if (splitRxNumbers.has(rx)) return false;
      if (zeroFillRxNumbers.has(rx)) return false;
      if (orderRxSet.size > 0 && !orderRxSet.has(rx)) return false;
      if (stageRxSet && !stageRxSet.has(rx)) return false;
      return true;
    };

    const rxNumbers: string[] = [];

    // Source 1: items array (OrderItems relation)
    if (params.row.items && Array.isArray(params.row.items)) {
      params.row.items.forEach((item: any) => {
        if (item.rxNumber && !rxNumbers.includes(item.rxNumber) && isActiveMed(String(item.rxNumber))) {
          rxNumbers.push(item.rxNumber);
        }
      });
    }

    // Source 2: metadata.billingData.medications array (multi-Rx orders)
    const medications = params.row.metadata?.billingData?.medications;
    if (medications && Array.isArray(medications)) {
      medications.forEach((med: any) => {
        if (med.rxNumber && !rxNumbers.includes(med.rxNumber) && isActiveMed(String(med.rxNumber))) {
          rxNumbers.push(med.rxNumber);
        }
      });
    }

    // Fallback to single rxNumber if no items/medications or they had no Rx numbers
    if (rxNumbers.length === 0 && params.row.rxNumber) {
      // Use the order's rx_number directly (already filtered by the split)
      params.row.rxNumber.split(',').forEach((rx: string) => {
        const trimmed = rx.trim();
        if (trimmed && !rxNumbers.includes(trimmed)) {
          rxNumbers.push(trimmed);
        }
      });
    }

    return (
      <Stack spacing={0.25} sx={{ py: 0.5 }}>
        <Typography
          variant="body2"
          sx={{
            color: 'primary.main',
            textDecoration: 'underline',
            cursor: 'pointer',
            fontWeight: 'medium',
            fontSize: '0.85rem',
            '&:hover': { color: 'primary.dark' },
          }}
          onClick={(e) => {
            e.stopPropagation();
            handlePatientClick(params.row);
          }}
        >
          {params.row.patientName}
        </Typography>
        {rxNumbers.length === 1 ? (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            Rx: {rxNumbers[0]}
          </Typography>
        ) : rxNumbers.length > 1 ? (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            Rx: {rxNumbers.join(', ')}
          </Typography>
        ) : (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            Rx: —
          </Typography>
        )}
      </Stack>
    );
  },
});

// Column 3.5: Qty (quantity) - narrow fixed width
export const createCompactQtyColumn = (): GridColDef => ({
  field: 'quantity',
  headerName: 'Qty',
  width: 60,
  headerAlign: 'center',
  align: 'center',
  renderCell: (params: GridRenderCellParams) => (
    <Typography variant="body2" sx={{ fontSize: '0.85rem', py: 0.5 }}>
      {params.row.quantity ?? '—'}
    </Typography>
  ),
});

/**
 * GP-1321: single source of truth for "who prescribed this order".
 *
 * The three queues that share the Drug / Prescriber column each used to read
 * their own field order. Clinical Verification and Packaging tried
 * `metadata.prescriber` FIRST — a key populated on ~10% of rows — while Product
 * Verification tried `metadata.prescriberName`, the reliably-populated one. That
 * divergence is why the SAME order showed a real doctor in Clinical Verification
 * and nothing one stage later. `prescriberName` leads here; every queue calls
 * this and no queue re-orders the fallbacks locally.
 *
 * Returns '' when nothing is populated — never a fabricated, name-shaped string.
 * The empty value is what makes the column's '—' branch reachable.
 */
export const resolvePrescriber = (order: any): string =>
  order?.metadata?.prescriberName ||
  order?.metadata?.prescriber ||
  order?.metadata?.billingData?.prescriber ||
  '';

/**
 * Render a prescriber value as a single "Dr. <surname>" line, or '—'.
 *
 * Some sources already carry the honorific, so the old unconditional `Dr. ${…}`
 * printed "Dr. Dr. Unknown". Strip it before prefixing — the same normalization
 * the Zebra label template already does (utils/zplTemplates.ts).
 */
export const formatPrescriberDisplay = (prescriber?: string | null): string => {
  const name = String(prescriber ?? '')
    .replace(/^Dr\.?\s*/i, '')
    .split(',')[0]
    .trim();
  return name ? `Dr. ${name}` : '—';
};

// Column 4: Drug/Prescriber (Drug Name + Prescriber) - flex
// IMPORTANT: Drug name is shown in full without truncation per user requirement
export const createCompactDrugPrescriberColumn = (queueStage?: string): GridColDef => ({
  field: 'drugName',
  headerName: 'Drug / Prescriber',
  flex: 1.5,
  minWidth: 200,
  renderCell: (params: GridRenderCellParams) => {
    const item = params.row;
    // Excludes drugs split to Troubleshooting and zero-fill drugs (whole qty
    // deferred to a BO- clone), and narrows to this queue's stage when the
    // per-drug signal is present. Shared with the row's "Send to task queue"
    // action so the two can never disagree (GP-1487).
    const medications = getQueueStageMedications(item, queueStage);
    const medicationCount = medications.length;

    // GP-1413: per-drug Clinical Verification progress — each drug is committed
    // the moment "Complete Drug N of M" is clicked
    // (ClinicalVerificationDialog.persistPartialClinicalVerification), so a
    // multi-drug order can sit in this queue partially verified instead of
    // showing every drug as needing review. Records are only written for
    // fully-verified drugs, so presence in the set = verified. Only the
    // Clinical Verification queue surfaces this (mirrors the GP-1252 treatment
    // for Product Verification in createUpcDinColumn); other queues untouched.
    const cvVerifiedRxSet = new Set<string>(
      queueStage === 'clinical_verification'
        ? ((item.metadata?.clinicalVerificationProgress?.drugVerifications || []) as any[])
            .map((d: any) => String(d?.rxNumber))
        : []
    );
    const cvVerifiedCount = (medications || []).filter((m: any) =>
      cvVerifiedRxSet.has(String(m.rxNumber))
    ).length;
    const hasPartialCvProgress = cvVerifiedCount > 0 && cvVerifiedCount < medicationCount;

    // GP-469: Fridge indicators were only rendered in the single-medication
    // branch, so multi-drug orders that contain a refrigerated drug (or a
    // cold-chain split) showed no fridge tag at all. Compute the flags once
    // and render them regardless of medication count.
    // Row shape varies across queue pages: some pre-compute top-level
    // hasColdChainSplit / temperatureGroup (PackagingQueue, StagingArea,
    // ReadyToShipQueue), others pass the raw order where only
    // metadata.coldChainSplit and temperatureGroup are populated
    // (ClinicalVerification, ProductVerification, ScheduleDelivery). Check
    // both paths so the fridge indicators render consistently everywhere.
    const showRefrigeratedIcon =
      item.isRefrigerated ||
      item.temperatureGroup === 'cold_chain' ||
      (medications || []).some((med: any) => med.isRefrigerated || med.temperatureGroup === 'cold_chain');
    const showColdChainSplitChip = !!item.hasColdChainSplit || !!item.metadata?.coldChainSplit;

    return (
      <Stack spacing={0.25} sx={{ py: 0.5 }}>
        {medicationCount > 1 ? (
          <>
            {(showRefrigeratedIcon || showColdChainSplitChip) && (
              <Stack direction="row" spacing={0.5} alignItems="center">
                {showRefrigeratedIcon && (
                  <Tooltip title="Refrigerated - Cold Chain" arrow>
                    <AcUnit sx={{ fontSize: '0.85rem', color: 'info.main' }} />
                  </Tooltip>
                )}
                {showColdChainSplitChip && (
                  <Tooltip title={`Contains fridge items (${item.coldChainOrderNumber || item.metadata?.coldChainSplit?.coldChainOrderNumber || 'CC order'}) — 2 bags, 1 delivery`} arrow>
                    <Chip
                      icon={<AcUnit sx={{ fontSize: '0.65rem' }} />}
                      label="+ Fridge"
                      size="small"
                      color="info"
                      variant="outlined"
                      sx={{ height: 18, fontSize: '0.6rem', fontWeight: 'bold' }}
                    />
                  </Tooltip>
                )}
              </Stack>
            )}
            {/* GP-1413: once a subset is clinically verified, show remaining-only
                progress so a resumed order reads "2 of 6 verified" instead of
                every drug appearing to need review. */}
            {hasPartialCvProgress && (
              <Typography variant="caption" color="success.main" fontWeight="bold" sx={{ fontSize: '0.7rem' }}>
                ✓ {cvVerifiedCount} OF {medicationCount} VERIFIED — {medicationCount - cvVerifiedCount} REMAINING
              </Typography>
            )}
            {medications.map((med: any, idx: number) => {
              // GP-1413: mark drugs already clinically verified (green check +
              // strike-through) so a resuming pharmacist sees at a glance what's left.
              const isCvVerified = cvVerifiedRxSet.has(String(med.rxNumber));
              return (
              <Stack key={idx} direction="row" spacing={0.5} alignItems="center">
                <Typography variant="caption" color={isCvVerified ? 'success.main' : 'text.secondary'} sx={{ fontSize: '0.65rem', minWidth: '12px' }}>
                  {isCvVerified ? '✓' : `${idx + 1}.`}
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight="medium"
                  sx={{ fontSize: '0.8rem', whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.3, color: isCvVerified ? 'success.main' : undefined, textDecoration: isCvVerified ? 'line-through' : undefined }}
                >
                  {med.medicationName} {med.strength || ''}
                </Typography>
                {(med.isRefrigerated || med.temperatureGroup === 'cold_chain') && (
                  <Tooltip title="Refrigerated - Cold Chain" arrow>
                    <AcUnit sx={{ fontSize: '0.75rem', color: 'info.main' }} />
                  </Tooltip>
                )}
              </Stack>
              );
            })}
          </>
        ) : (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography
              variant="body2"
              fontWeight="medium"
              sx={{
                fontSize: '0.85rem',
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                lineHeight: 1.3,
              }}
            >
              {/* Prefer the filtered medications[0] name when only one drug remains
                  (e.g. zero-fill drugs were filtered out). item.drugName is the
                  pre-filter aggregate string and would otherwise show all original
                  drugs comma-separated. */}
              {medicationCount === 1 && medications?.[0]?.medicationName
                ? `${medications[0].medicationName} ${medications[0].strength || ''}`.trim()
                : item.drugName}
            </Typography>
            {showRefrigeratedIcon && (
              <Tooltip title="Refrigerated - Cold Chain" arrow>
                <AcUnit sx={{ fontSize: '0.85rem', color: 'info.main' }} />
              </Tooltip>
            )}
            {showColdChainSplitChip && (
              <Tooltip title={`Contains fridge items (${item.coldChainOrderNumber || 'CC order'}) — 2 bags, 1 delivery`} arrow>
                <Chip
                  icon={<AcUnit sx={{ fontSize: '0.65rem' }} />}
                  label="+ Fridge"
                  size="small"
                  color="info"
                  variant="outlined"
                  sx={{ height: 18, fontSize: '0.6rem', fontWeight: 'bold' }}
                />
              </Tooltip>
            )}
            {(item.deliveryType === 'LtoM – MM' || item.deliveryType === 'LTOM_MM') && (
              <Tooltip title="Middle Mile — London to Mississauga. Shipment in Mississauga Descartes." arrow>
                <Chip
                  label="L2M"
                  size="small"
                  color="secondary"
                  sx={{ height: 18, fontSize: '0.6rem', fontWeight: 'bold' }}
                />
              </Tooltip>
            )}
            {(item.sourceType === 'new_start_iv' || item.sourceType === 'new_start_home_del' || item.sourceType === 'patient360_new_start') && (
              <Tooltip title="New Start Patient — Review New Start Checklist" arrow>
                <Chip
                  icon={<Assignment sx={{ fontSize: '0.65rem' }} />}
                  label="New Start"
                  size="small"
                  color="warning"
                  sx={{ height: 18, fontSize: '0.6rem', fontWeight: 'bold' }}
                />
              </Tooltip>
            )}
          </Stack>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
          {formatPrescriberDisplay(item.prescriber)}
        </Typography>
      </Stack>
    );
  },
});

// Column 5: Delivery Date (with color) - 130px
// Header is explicitly "Delivery Date" (not just "Delivery") to disambiguate it
// from the adjacent non-sortable "Delivery Type" column — they previously shared
// the label "Delivery", so users clicked the wrong (un-sortable) header and
// thought date sorting was broken. This column is server-sortable by clicking.
export const createCompactDeliveryColumn = (): GridColDef => ({
  field: 'deliveryDate',
  headerName: 'Delivery Date',
  width: 130,
  renderCell: (params: GridRenderCellParams) => {
    if (!params.row.deliveryDate) {
      return <Typography variant="body2" color="text.secondary">—</Typography>;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const deliveryDate = new Date(params.row.deliveryDate.toString().split('T')[0]);
    deliveryDate.setHours(0, 0, 0, 0);
    const daysUntil = Math.ceil((deliveryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    const color = getAgingBucketColor(daysUntil);

    // Get urgency label
    let urgencyLabel = '';
    let urgencyColor: 'error' | 'warning' | 'info' | 'default' = 'default';
    if (daysUntil < 0) {
      urgencyLabel = 'OVERDUE';
      urgencyColor = 'error';
    } else if (daysUntil === 0) {
      urgencyLabel = 'TODAY';
      urgencyColor = 'error';
    } else if (daysUntil === 1) {
      urgencyLabel = 'TOMORROW';
      urgencyColor = 'warning';
    }

    return (
      <Stack spacing={0.25} sx={{ py: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 'bold', color, fontSize: '0.85rem' }}>
          {formatDateOnly(params.row.deliveryDate.toString().split('T')[0])}
        </Typography>
        {urgencyLabel && (
          <Chip
            label={urgencyLabel}
            size="small"
            color={urgencyColor}
            sx={{ height: 18, fontSize: '0.65rem', fontWeight: 'bold' }}
          />
        )}
        {params.row.temperatureGroup === 'cold_chain' && (
          <Chip
            icon={<AcUnit sx={{ fontSize: '0.7rem' }} />}
            label="Cold Chain"
            size="small"
            color="info"
            sx={{ height: 18, fontSize: '0.6rem', fontWeight: 'bold' }}
          />
        )}
        {params.row.temperatureGroup === 'room_temp' && (
          <Chip
            label="Room Temp"
            size="small"
            sx={{ height: 18, fontSize: '0.6rem', fontWeight: 'bold', bgcolor: panelNeutralHover }}
          />
        )}
        {/* GP-75: Prominent 2-bag indicator for split orders */}
        {(params.row.temperatureGroup === 'cold_chain' || params.row.hasColdChainSplit) && (
          <Chip
            icon={<Inventory2Outlined sx={{ fontSize: '0.7rem' }} />}
            label="2 BAGS"
            size="small"
            color="warning"
            sx={{ height: 20, fontSize: '0.65rem', fontWeight: 'bold' }}
          />
        )}
      </Stack>
    );
  },
});

// Column 6: Notes (stacked with previews and edit buttons) - 200px
export interface CompactNotesColumnHandlers {
  setSelectedItemForNotes: (item: any) => void;
  setOrderNoteDialogOpen: (open: boolean) => void;
  setSelectedItemForDeliveryInstructions: (item: any) => void;
  setDeliveryInstructionsDialogOpen: (open: boolean) => void;
  setClinicalNoteDialogOpen: (open: boolean) => void;
}

// Single note row component for the stacked notes column
const NoteRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  note: string | undefined;
  color: 'primary' | 'info' | 'success';
  onEdit: () => void;
}> = ({ icon, label, note, color, onEdit }) => (
  <Stack
    direction="row"
    spacing={0.5}
    alignItems="center"
    sx={{
      py: 0.25,
      cursor: 'pointer',
      borderRadius: 0.5,
      '&:hover': { bgcolor: 'action.hover' },
    }}
    onClick={(e) => {
      e.stopPropagation();
      onEdit();
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', color: note ? `${color}.main` : 'action.disabled' }}>
      {icon}
    </Box>
    {note ? (
      <Tooltip title={note}>
        <Typography
          variant="caption"
          sx={{
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: '0.7rem',
            color: 'text.secondary',
          }}
        >
          {note}
        </Typography>
      </Tooltip>
    ) : (
      <Typography
        variant="caption"
        sx={{
          flex: 1,
          fontSize: '0.7rem',
          color: 'text.disabled',
          fontStyle: 'italic',
        }}
      >
        Add {label}...
      </Typography>
    )}
    <IconButton size="small" sx={{ p: 0.25, opacity: 0.6, '&:hover': { opacity: 1 } }}>
      <AddComment sx={{ fontSize: 14 }} color={note ? color : 'action'} />
    </IconButton>
  </Stack>
);

export const createCompactNotesColumn = (handlers: CompactNotesColumnHandlers): GridColDef => ({
  field: 'notes',
  headerName: 'Notes',
  width: 200,
  sortable: false,
  renderCell: (params: GridRenderCellParams) => {
    const internalNote = params.row.internalNote;
    const deliveryInstructions = params.row.deliveryInstructions;
    const clinicalNote = params.row.clinicalNote;

    return (
      <Stack spacing={0} sx={{ py: 0.5, width: '100%' }}>
        <NoteRow
          icon={<StickyNote2 sx={{ fontSize: 14 }} />}
          label="order note"
          note={internalNote}
          color="primary"
          onEdit={() => {
            handlers.setSelectedItemForNotes(params.row);
            handlers.setOrderNoteDialogOpen(true);
          }}
        />
        <NoteRow
          icon={<LocalShipping sx={{ fontSize: 14 }} />}
          label="driver instructions"
          note={deliveryInstructions}
          color="info"
          onEdit={() => {
            handlers.setSelectedItemForDeliveryInstructions(params.row);
            handlers.setDeliveryInstructionsDialogOpen(true);
          }}
        />
        <NoteRow
          icon={<Science sx={{ fontSize: 14 }} />}
          label="clinical note"
          note={clinicalNote}
          color="success"
          onEdit={() => {
            handlers.setSelectedItemForNotes(params.row);
            handlers.setClinicalNoteDialogOpen(true);
          }}
        />
      </Stack>
    );
  },
});

// Column 7: Status/Actions (Lock + Assigned + Actions) - 320px
export type LockableEntityType = 'order' | 'billing_item';

export interface CompactStatusActionsColumnHandlers {
  isEntityLocked: (entityType: LockableEntityType, entityId: string) => { isLocked: boolean; lockedBy?: string; lockedAt?: string };
  userEmail: string;
  staffUsers: Array<{ id: string; name: string }>;
  userIdToNameMap?: Map<string, string>;
  handleClinicalAssignmentChange: (orderId: string, userId: string | null) => void;
  handleViewOrder: (orderId: string) => void;
  setSelectedItemForContext: (item: any) => void;
  setContextNotesDrawerOpen: (open: boolean) => void;
  handleOpenVerificationDialog: (row: any) => void;
  canPerformClinicalReview: boolean;
  resolveUserName: (emailOrId: string | undefined) => string;
  activePharmacyId: string;
  fetchQueue: () => void;
  openTroubleshootingDialog?: (row: any) => void;
  /**
   * When set, renders a pharmacist-gated "Cancel" action that opens a chooser
   * (Cancel Order / Cancel Subscription / Cancel Delivery). Gated by the same
   * `canPerformClinicalReview` flag as the Clinical Review action so it is
   * greyed-out for non-pharmacists (GP-1118).
   */
  openCancelDialog?: (row: any) => void;
  /**
   * GP-1398: when set, renders a "Send Message" action that opens the patient's
   * SMS conversation in place. The Billing Queue has had this since GP-1194;
   * Clinical Verification did not, so a pharmacist counselling an order had to
   * leave the queue for `/order-fulfillment/billing-queue` just to read a thread.
   * Same drawer, same patient — one column definition away.
   */
  openMessageDialog?: (row: any) => void;
  /**
   * GP-1487: when set, renders a "Send to task queue" action that queues
   * counseling for this row's drugs WITHOUT opening the order. The queue had no
   * such control at all, so dispatching a week of new starts meant a full
   * drill-in per order — an exclusive order lock plus ~20 hydration calls each.
   */
  sendToTaskQueue?: (row: any) => void;
  /**
   * GP-1487: Rx numbers that already have an OPEN counseling task, keyed by
   * order id — resolved for the whole page in one batch call by the caller, never
   * per row. Used to mark the action as already done so a second reviewer
   * working the same list doesn't re-send an order.
   */
  openCounselingRxByOrder?: Map<string, Set<string>>;
  /** GP-1487: order ids whose send is in flight (spinner + disabled). */
  sendingToTaskQueueOrderIds?: Set<string>;
  /**
   * GP-1487: the Rx numbers a row would send. Supplied by the caller (which owns
   * the queue-stage rules) so this column never re-derives the drug list.
   */
  getTaskQueueRxNumbers?: (row: any) => string[];
}

export const createCompactStatusActionsColumn = (handlers: CompactStatusActionsColumnHandlers): GridColDef => ({
  field: 'actions',
  headerName: 'Assigned / Actions',
  // GP-1398: +36px for the Send Message action. GP-1487: +36px again for Send to
  // task queue, only when that handler is wired. The assignee Autocomplete is a
  // fixed 130px, so every icon added here has to be paid for in column width or
  // the last one clips behind the sticky edge.
  width: 356 + (handlers.sendToTaskQueue ? 36 : 0),
  sortable: false,
  cellClassName: 'actions-column',
  headerClassName: 'actions-column',
  renderCell: (params: GridRenderCellParams) => {
    const lockStatus = handlers.isEntityLocked('order', params.row.id);
    const isLockedByOther = lockStatus.isLocked && lockStatus.lockedBy !== handlers.userEmail;
    const currentAssignee = params.row.clinicalAssignedTo || null;
    const selectedOption = currentAssignee
      ? handlers.staffUsers.find(u => u.id === currentAssignee) ||
        { id: currentAssignee, name: handlers.userIdToNameMap?.get(currentAssignee) || currentAssignee }
      : null;

    return (
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ py: 0.5 }}>
        {/* Lock indicator - only show if locked */}
        {lockStatus.isLocked && (
          <Tooltip title={lockStatus.lockedBy === handlers.userEmail ? 'Locked by you' : `Locked by ${handlers.resolveUserName(lockStatus.lockedBy)}`}>
            <LockIcon
              fontSize="small"
              sx={{
                color: lockStatus.lockedBy === handlers.userEmail ? 'success.main' : 'warning.main',
                fontSize: '1rem',
              }}
            />
          </Tooltip>
        )}

        {/* Assignee dropdown - wider to show full names */}
        <Autocomplete
          size="small"
          options={handlers.staffUsers}
          getOptionLabel={(option) => option.name || option.id}
          getOptionKey={(option) => option.id}
          value={selectedOption}
          onChange={(_, newValue) => {
            handlers.handleClinicalAssignmentChange(params.row.id, newValue?.id || null);
          }}
          renderInput={(inputParams) => (
            <TextField
              {...inputParams}
              variant="outlined"
              placeholder="Assign"
              sx={{ width: 130 }}
            />
          )}
          sx={{
            '& .MuiInputBase-root': {
              py: 0,
              fontSize: '0.75rem',
              minHeight: 28,
            },
            '& .MuiAutocomplete-input': {
              fontSize: '0.75rem',
            },
          }}
          isOptionEqualToValue={(option, value) => option.id === value?.id}
          onClick={(e) => e.stopPropagation()}
        />

        {/* Action buttons - Order: Queue-specific action, View Details, Context Notes, Troubleshooting */}
        <Tooltip title={
          !handlers.canPerformClinicalReview
            ? 'No permission for clinical review'
            : isLockedByOther
              ? `Locked by ${handlers.resolveUserName(lockStatus.lockedBy)}`
              : 'Clinical Review'
        }>
          <span>
            <IconButton
              size="small"
              color={isLockedByOther ? 'warning' : 'primary'}
              disabled={!handlers.canPerformClinicalReview}
              onClick={(e) => {
                e.stopPropagation();
                handlers.handleOpenVerificationDialog(params.row);
              }}
              sx={{ p: 0.5 }}
            >
              {isLockedByOther ? <LockIcon fontSize="small" /> : <Science fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
        {/* GP-1487: send this order's clinical-verification drugs to the Task
            Queue in place — no order lock, no order hydration. Idempotent per Rx
            server-side, so a double click cannot double-queue. */}
        {handlers.sendToTaskQueue && (() => {
          const targetRxNumbers = handlers.getTaskQueueRxNumbers?.(params.row) ?? [];
          const openRxNumbers = handlers.openCounselingRxByOrder?.get(params.row.id);
          const alreadyQueued =
            targetRxNumbers.length > 0 &&
            targetRxNumbers.every((rx) => openRxNumbers?.has(rx));
          const sending = handlers.sendingToTaskQueueOrderIds?.has(params.row.id) ?? false;
          // Nothing resolvable to queue: say so rather than offering an action
          // that would have to invent an Rx number.
          const nothingToQueue = targetRxNumbers.length === 0;
          // Same RBAC gate as the Clinical Review flask above (GP-1118
          // convention): queuing counselling was pharmacist-only before this
          // action existed, and the backend has no gate of its own.
          const noPermission = handlers.canPerformClinicalReview === false;

          return (
            <Tooltip
              title={
                noPermission
                  ? 'No permission for clinical review'
                  : nothingToQueue
                    ? 'No prescription resolved on this row — open the order to queue counseling'
                    : alreadyQueued
                      ? `Already in the task queue (Rx ${targetRxNumbers.join(', ')})`
                      : 'Send to task queue'
              }
            >
              <span>
                <IconButton
                  size="small"
                  color={alreadyQueued ? 'success' : 'primary'}
                  disabled={sending || alreadyQueued || nothingToQueue || noPermission}
                  aria-label={alreadyQueued ? 'Already in task queue' : 'Send to task queue'}
                  onClick={(e) => {
                    e.stopPropagation();
                    handlers.sendToTaskQueue!(params.row);
                  }}
                  sx={{ p: 0.5 }}
                >
                  {sending ? (
                    <CircularProgress size={16} />
                  ) : alreadyQueued ? (
                    <TaskQueuedIcon fontSize="small" />
                  ) : (
                    <TaskQueueIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          );
        })()}
        <Tooltip title="View Order Details">
          <IconButton
            size="small"
            color="info"
            onClick={(e) => {
              e.stopPropagation();
              handlers.handleViewOrder(params.row.id);
            }}
            sx={{ p: 0.5 }}
          >
            <Description fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Context Notes">
          <IconButton
            size="small"
            color="info"
            onClick={(e) => {
              e.stopPropagation();
              handlers.setSelectedItemForContext(params.row);
              handlers.setContextNotesDrawerOpen(true);
            }}
            sx={{ p: 0.5 }}
          >
            <Info fontSize="small" />
          </IconButton>
        </Tooltip>
        {/* GP-1398: the patient conversation, opened from the queue the
            pharmacist is already working — no trip to the Billing Queue. */}
        {handlers.openMessageDialog && (
          <Tooltip title="Send Message" TransitionProps={{ timeout: 0 }}>
            <IconButton
              size="small"
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                handlers.openMessageDialog!(params.row);
              }}
              sx={{ p: 0.5 }}
            >
              <SmsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {handlers.openTroubleshootingDialog && (
          <Tooltip title="Send to Troubleshooting">
            <IconButton
              size="small"
              color="warning"
              onClick={(e) => {
                e.stopPropagation();
                handlers.openTroubleshootingDialog!(params.row);
              }}
              sx={{ p: 0.5 }}
            >
              <BuildIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {/* Cancel action — pharmacist-gated (same gate as Clinical Review).
            Opens a chooser: Cancel Order / Cancel Subscription / Cancel Delivery. */}
        {handlers.openCancelDialog && (
          <Tooltip title={
            !handlers.canPerformClinicalReview
              ? 'No permission to cancel'
              : 'Cancel Order / Delivery'
          }>
            <span>
              <IconButton
                size="small"
                color="error"
                disabled={!handlers.canPerformClinicalReview}
                onClick={(e) => {
                  e.stopPropagation();
                  handlers.openCancelDialog!(params.row);
                }}
                sx={{ p: 0.5 }}
              >
                <CancelOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Stack>
    );
  },
});

// ============ DELIVERY TYPE HELPERS ============
// These are shared utilities for displaying delivery type across queues

type DeliveryTypeColor = 'info' | 'warning' | 'secondary' | 'error' | 'success' | 'default';

/**
 * Get display label for a delivery type
 * Supports both new and legacy enum values for backward compatibility
 */
export const getDeliveryTypeLabel = (deliveryType: string | null | undefined): string => {
  if (!deliveryType) return 'Not Set';

  const labels: Record<string, string> = {
    // New enum values
    'InHouse': 'InHouse',
    'Courier': 'Courier',
    '3rd_Party': '3rd Party',
    'GS Pharmacy – AEX': 'GS Pharmacy – AEX',
    'LtoM – MM': 'LtoM',
    // Old enum values (for backward compatibility)
    'In-house': 'InHouse',
    'FedEx': 'Courier',
    'Purolator': 'Courier',
    'Apple Express': '3rd Party',
    'London L to M': 'LtoM'
  };
  return labels[deliveryType] || deliveryType;
};

/**
 * Get MUI chip color for a delivery type
 * Supports both new and legacy enum values for backward compatibility
 */
export const getDeliveryTypeColor = (deliveryType: string | null | undefined): DeliveryTypeColor => {
  if (!deliveryType) return 'default';

  const colors: Record<string, DeliveryTypeColor> = {
    // New enum values
    'InHouse': 'info',
    'Courier': 'warning',
    '3rd_Party': 'secondary',
    'GS Pharmacy – AEX': 'info',
    'LtoM – MM': 'success',
    // Old enum values (for backward compatibility)
    'In-house': 'info',
    'FedEx': 'warning',
    'Purolator': 'warning',
    'Apple Express': 'secondary',
    'London L to M': 'success'
  };
  return colors[deliveryType] || 'default';
};

// ============ COMPACT DELIVERY TYPE COLUMN ============

export interface CompactDeliveryTypeColumnHandlers {
  handleOpenDeliveryTypeModal?: (item: any) => void;
  readOnly?: boolean;
}

export const createCompactDeliveryTypeColumn = (handlers: CompactDeliveryTypeColumnHandlers): GridColDef => ({
  field: 'deliveryType',
  headerName: 'Delivery Type',
  // GP-1347: wide enough for "Nursing Home – <home>" — the home name is the point
  // of the chip, so it must not be the part that gets ellipsised away.
  width: 170,
  sortable: false,
  renderCell: (params: GridRenderCellParams) => {
    const orderType = params.row.orderType || 'Delivery';

    if (orderType === 'Pickup') {
      return (
        <Tooltip title="Delivery type not applicable for pickup orders">
          <Chip
            label="Pickup"
            variant="outlined"
            size="small"
            sx={{
              height: 22,
              fontSize: '0.7rem',
              opacity: 0.6,
              cursor: 'help'
            }}
          />
        </Tooltip>
      );
    }

    // GP-1314: Nursing Home is a third fulfilment mode and carries NO courier
    // delivery type by design (the backend short-circuits NH out of Descartes
    // scheduling), so it used to fall through to a misleading "Not Set". Show
    // the mode itself; NEVER stamp a deliveryType on the order to fix this.
    // GP-1347: the chip names the destination home — see NursingHomeDeliveryTypeChip.
    if (isNursingHomeOrder(params.row)) {
      return <NursingHomeDeliveryTypeChip row={params.row} />;
    }

    const deliveryType = params.row.deliveryType;
    const label = getDeliveryTypeLabel(deliveryType);
    const color = getDeliveryTypeColor(deliveryType);

    // GP-166: Format delivery address from snapshot
    const snapshot = params.row.deliveryAddressSnapshot;
    const addressStr = snapshot
      ? [snapshot.street, snapshot.city, snapshot.province].filter(Boolean).join(', ')
      : '';

    return (
      <Stack spacing={0} sx={{ py: 0.5 }}>
        <Stack direction="row" spacing={0.25} alignItems="center">
          <Tooltip title={`Delivery Type: ${label}`}>
            <Chip
              label={label}
              color={color}
              size="small"
              sx={{
                height: 22,
                fontSize: '0.7rem',
                fontWeight: 'medium',
                maxWidth: 70,
                '& .MuiChip-label': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }
              }}
            />
          </Tooltip>
          {!handlers.readOnly && handlers.handleOpenDeliveryTypeModal && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handlers.handleOpenDeliveryTypeModal?.(params.row);
              }}
              sx={{
                p: 0.25,
                opacity: 0.6,
                '&:hover': { opacity: 1 }
              }}
            >
              <EditIcon sx={{ fontSize: 14 }} />
            </IconButton>
          )}
        </Stack>
        {addressStr && (
          <Tooltip title={addressStr}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: '0.6rem', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 95 }}
            >
              {addressStr}
            </Typography>
          </Tooltip>
        )}
      </Stack>
    );
  },
});

// ============ ALL ORDERS VIEW COMPACT COLUMNS ============
// Compact columns specific to the All Orders read-only view

// Status color mapping for All Orders view
const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  const colorMap: Record<string, any> = {
    'draft': 'default',
    'submitted': 'info',
    'confirmed': 'primary',
    'clinically_verified': 'primary',
    'packaged': 'primary',
    'verified': 'primary',
    'ready_to_ship': 'success',
    'staged': 'success',
    'out_for_delivery': 'info',
    'delivered': 'success',
    'completed': 'success',
    'cancelled': 'error',
    'on_hold': 'warning',
  };
  return colorMap[status] || 'default';
};

// Stage route mapping for clickable stage links
const getStageRoute = (workflowStage: string): string => {
  const stageRouteMap: Record<string, string> = {
    'billing': '/order-fulfillment/billing-queue',
    'clinical_verification': '/order-fulfillment/clinical-verification',
    'packaging': '/order-fulfillment/packaging',
    'product_verification': '/order-fulfillment/product-verification',
    'ready_to_ship': '/order-fulfillment/ready-to-ship',
    'staging': '/order-fulfillment/staging-area',
    'out_for_delivery': DELIVERY_STATUS_ROUTE,
    // GP-1379 (batch-5 review): this map is the SECOND copy of the queue
    // routing — the same gaps OrdersTable's copy had. A ready-for-pickup /
    // missed-delivery order is worked from the Delivery Status queue; unmapped
    // they fell through to the Billing Queue default.
    'ready_for_pickup': DELIVERY_STATUS_ROUTE,
    'delivery_missed': DELIVERY_STATUS_ROUTE,
    'clinic_scheduling': '/order-fulfillment/schedule-delivery',
  };
  return stageRouteMap[workflowStage] || '/order-fulfillment/billing-queue';
};

// Format display status from raw status
const formatDisplayStatus = (status: string): string => {
  return status.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
};

// Drug + Quantity column (flex, minWidth 220px) - Shows full drug name with quantity stacked below
// IMPORTANT: Drug name is shown in full without truncation per user requirement
//
// Multi-drug orders (GP-624): the denormalized `order.quantity` column is the
// SUM of every medication's quantity, so a two-Rx order (e.g. two Abrilada Rx of
// qty 3) showed a single misleading "Qty: 6" next to two drug names. When the row
// carries a per-drug `medications` array we render each drug with ITS OWN quantity
// (and DIN) instead of the cross-drug sum. Single-drug rows and rows without a
// medications array (other queues) keep the original single-line rendering.
export const createCompactDrugQtyColumn = (): GridColDef => ({
  field: 'drugName',
  headerName: 'Drug / Qty',
  flex: 1.5,
  minWidth: 220,
  renderCell: (params: GridRenderCellParams) => {
    const medications = Array.isArray(params.row.medications) ? params.row.medications : [];

    // Multi-drug: attribute each quantity to its own drug (no cross-drug sum).
    if (medications.length > 1) {
      return (
        <Stack spacing={0.5} sx={{ py: 0.5 }}>
          {medications.map((med: any, index: number) => (
            <Box key={med?.rxNumber ?? index}>
              <Typography
                variant="body2"
                fontWeight="medium"
                sx={{
                  fontSize: '0.85rem',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  lineHeight: 1.3,
                }}
              >
                {med?.medicationName || '—'}
              </Typography>
              {(med?.quantity != null || med?.rxNumber || med?.din) && (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                  {[
                    med?.quantity != null ? `Qty: ${med.quantity}` : null,
                    // GP-1411: drugs in one order can be on different prescriptions;
                    // the Patient/Rx column collapses to the order-level rx_number,
                    // so each drug line carries its own Rx here.
                    med?.rxNumber ? `Rx: ${med.rxNumber}` : null,
                    med?.din ? `DIN: ${med.din}` : null,
                  ].filter(Boolean).join(' | ')}
                </Typography>
              )}
            </Box>
          ))}
        </Stack>
      );
    }

    // Single-drug (or no per-drug data): unchanged single-line rendering.
    return (
      <Stack spacing={0.25} sx={{ py: 0.5 }}>
        <Typography
          variant="body2"
          fontWeight="medium"
          sx={{
            fontSize: '0.85rem',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            lineHeight: 1.3,
          }}
        >
          {params.row.drugName || '—'}
        </Typography>
        {(params.row.quantity != null || params.row.din) && (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            {[
              params.row.quantity != null ? `Qty: ${params.row.quantity}` : null,
              params.row.din ? `DIN: ${params.row.din}` : null,
            ].filter(Boolean).join(' | ')}
          </Typography>
        )}
      </Stack>
    );
  },
});

// Status + Stage column (150px) - Shows status chip with clickable stage link below
export interface CompactStatusStageColumnHandlers {
  navigate: (url: string) => void;
}

export const createCompactStatusStageColumn = (handlers: CompactStatusStageColumnHandlers): GridColDef => ({
  field: 'status',
  headerName: 'Status / Stage',
  width: 150,
  renderCell: (params: GridRenderCellParams) => {
    const stage = params.row.workflowStage || 'billing';
    const route = getStageRoute(stage);
    const orderId = params.row.id;
    // GP-1379 (batch-5 review): queue search boxes match patient name / order
    // number / RX — never the internal UUID. Send the order NUMBER as well;
    // `orderId` stays for the queues that still read it. Mirrors OrdersTable's
    // fixed "Click to go to queue" link.
    const linkParams = new URLSearchParams();
    if (params.row.orderNumber) linkParams.set('orderNumber', params.row.orderNumber);
    linkParams.set('orderId', orderId ?? '');

    // If stage is clinic_scheduling, add tab parameter for troubleshooting
    if (stage === 'clinic_scheduling') {
      linkParams.set('tab', 'troubleshooting');
    } else if (route === DELIVERY_STATUS_ROUTE) {
      // The Delivery Status queue splits pickups and deliveries into tabs that
      // exclude each other, so the landing tab has to follow the order's own
      // fulfilment method. Unknown method → no hint; the queue keeps its default.
      const tab = deliveryStatusTabFor(params.row);
      if (tab) linkParams.set('tab', tab);
    }
    const fullUrl = `${route}?${linkParams.toString()}`;

    // Get display stage name
    const stageDisplayMap: Record<string, string> = {
      'clinic_scheduling': 'Scheduling',
      'clinical_verification': 'Clinical',
      'product_verification': 'Verification',
      'ready_to_ship': 'Ready Ship',
      'out_for_delivery': 'Out for Delivery',
    };
    // GP-1405: a pickup order must not be labelled with a shipping stage. Same
    // row already picks its Delivery Status sub-tab from `deliveryMethod` just
    // above — the label now reads the same field, so the two can't contradict.
    const stageDisplay =
      pickupStageLabelForOrder(params.row) ?? stageDisplayMap[stage] ?? stage;

    return (
      <Stack spacing={0.5} sx={{ py: 0.5 }}>
        <Chip
          label={formatDisplayStatus(params.row.status)}
          color={getStatusColor(params.row.status)}
          size="small"
          sx={{ height: 20, fontSize: '0.7rem' }}
        />
        <Typography
          variant="caption"
          component="a"
          href={fullUrl}
          onClick={(e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            handlers.navigate(fullUrl);
          }}
          sx={{
            color: 'primary.main',
            textDecoration: 'underline',
            cursor: 'pointer',
            fontSize: '0.75rem',
            '&:hover': { color: 'primary.dark' },
          }}
        >
          → {stageDisplay}
        </Typography>
      </Stack>
    );
  },
});

// Read-only notes column for All Orders (200px) - Shows notes without edit capabilities
export const createCompactNotesReadOnlyColumn = (): GridColDef => ({
  field: 'notes',
  headerName: 'Notes',
  width: 200,
  sortable: false,
  renderCell: (params: GridRenderCellParams) => {
    const deliveryOrderNote = params.row.internalNote;
    const deliveryInstructions = params.row.deliveryInstructions;
    const clinicalNote = params.row.clinicalNote;

    const hasAnyNote = deliveryOrderNote || deliveryInstructions || clinicalNote;

    if (!hasAnyNote) {
      return (
        <Typography variant="caption" color="text.disabled" sx={{ py: 0.5 }}>
          No notes
        </Typography>
      );
    }

    return (
      <Stack spacing={0.25} sx={{ py: 0.5, width: '100%' }}>
        {deliveryOrderNote && (
          <Tooltip title={deliveryOrderNote}>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <StickyNote2 sx={{ fontSize: 12, color: 'primary.main' }} />
              <Typography
                variant="caption"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: '0.7rem',
                  color: 'text.secondary',
                  maxWidth: 160,
                }}
              >
                {deliveryOrderNote}
              </Typography>
            </Stack>
          </Tooltip>
        )}
        {deliveryInstructions && (
          <Tooltip title={deliveryInstructions}>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <LocalShipping sx={{ fontSize: 12, color: 'info.main' }} />
              <Typography
                variant="caption"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: '0.7rem',
                  color: 'text.secondary',
                  maxWidth: 160,
                }}
              >
                {deliveryInstructions}
              </Typography>
            </Stack>
          </Tooltip>
        )}
        {clinicalNote && (
          <Tooltip title={clinicalNote}>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Science sx={{ fontSize: 12, color: 'success.main' }} />
              <Typography
                variant="caption"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: '0.7rem',
                  color: 'text.secondary',
                  maxWidth: 160,
                }}
              >
                {clinicalNote}
              </Typography>
            </Stack>
          </Tooltip>
        )}
      </Stack>
    );
  },
});

// Simple View action column for All Orders (60px)
export interface AllOrdersActionsColumnHandlers {
  handleViewOrder: (orderId: string) => void;
}

export const createAllOrdersActionsColumn = (handlers: AllOrdersActionsColumnHandlers): GridColDef => ({
  field: 'actions',
  headerName: '',
  width: 60,
  sortable: false,
  align: 'center',
  cellClassName: 'actions-column',
  headerClassName: 'actions-column',
  renderCell: (params: GridRenderCellParams) => (
    <Tooltip title="View Details">
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          handlers.handleViewOrder(params.row.id);
        }}
        sx={{ p: 0.5 }}
      >
        <Description fontSize="small" color="info" />
      </IconButton>
    </Tooltip>
  ),
});

// ============ SCHEDULE DELIVERY QUEUE COMBINED COLUMN ============
// Combines Patient/Rx + Address + Descartes Status into a single stacked column

/**
 * Helper to parse address and extract city for bolding
 * Canadian address format: "123 Main St, Toronto, ON M5V 1A1"
 * Returns { beforeCity, city, afterCity } or null if can't parse
 */
const parseAddressForCity = (address: string): { beforeCity: string; city: string; afterCity: string } | null => {
  if (!address || address === 'No address') return null;

  // Try to match pattern: street, CITY, PROVINCE POSTAL
  // Canadian provinces: ON, QC, BC, AB, MB, SK, NS, NB, NL, PE, NT, YT, NU
  const provincePattern = /,\s*([^,]+),\s*(ON|QC|BC|AB|MB|SK|NS|NB|NL|PE|NT|YT|NU|Ontario|Quebec|British Columbia|Alberta|Manitoba|Saskatchewan|Nova Scotia|New Brunswick|Newfoundland|Prince Edward Island)\s/i;
  const match = address.match(provincePattern);

  if (match && match[1]) {
    const city = match[1].trim();
    const cityIndex = address.indexOf(match[0]);
    const beforeCity = address.substring(0, cityIndex + 2); // Include ", "
    const afterCity = address.substring(cityIndex + 2 + city.length);
    return { beforeCity, city, afterCity };
  }

  // Fallback: try splitting by commas and assume city is second part
  const parts = address.split(',').map(p => p.trim());
  if (parts.length >= 2) {
    const beforeCity = parts[0] + ', ';
    const city = parts[1];
    const afterCity = parts.length > 2 ? ', ' + parts.slice(2).join(', ') : '';
    return { beforeCity, city, afterCity };
  }

  return null;
};

/**
 * Creates a combined column for Schedule Delivery Queue that shows:
 * - Row 1: Patient name (clickable)
 * - Row 2: Rx number(s)
 * - Row 3: Address (full, with city bolded)
 * - Row 4: Descartes ID chip (if exists)
 *
 * This reduces the number of columns and eliminates horizontal scrolling.
 */
export const createCompactPatientAddressColumn = (
  handlePatientClick: (row: any) => void
): GridColDef => ({
  field: 'patientInfo',
  headerName: 'Patient / Address',
  width: 300,
  minWidth: 250,
  sortable: false,
  renderCell: (params: GridRenderCellParams) => {
    const row = params.row;
    const address = row.address || row.deliveryAddress || row.patientAddress || row.defaultAddress || 'No address';

    // Collect Rx numbers from multiple sources
    const rxNumbers: string[] = [];

    // Source 1: items array (OrderItems relation)
    if (row.items && Array.isArray(row.items)) {
      row.items.forEach((item: any) => {
        if (item.rxNumber && !rxNumbers.includes(item.rxNumber)) {
          rxNumbers.push(item.rxNumber);
        }
      });
    }

    // Source 2: metadata.billingData.medications array (multi-Rx orders)
    if (row.metadata?.billingData?.medications && Array.isArray(row.metadata.billingData.medications)) {
      row.metadata.billingData.medications.forEach((med: any) => {
        if (med.rxNumber && !rxNumbers.includes(med.rxNumber)) {
          rxNumbers.push(med.rxNumber);
        }
      });
    }

    // Fallback to single rxNumber
    if (rxNumbers.length === 0 && row.rxNumber) {
      rxNumbers.push(row.rxNumber);
    }

    // Parse address to bold the city
    const parsedAddress = parseAddressForCity(address);

    return (
      <Stack spacing={0.25} sx={{ py: 0.5, width: '100%', overflow: 'hidden' }}>
        {/* Row 1: Patient name (clickable) */}
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            cursor: row.patientId ? 'pointer' : 'default',
            '&:hover': row.patientId ? { textDecoration: 'underline', color: 'primary.main' } : {},
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (row.patientId) handlePatientClick(row);
          }}
        >
          {row.patientName || '—'}
        </Typography>

        {/* Row 2: Rx number(s) */}
        <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.75rem' }}>
          Rx: {rxNumbers.length > 0 ? rxNumbers.join(', ') : '—'}
        </Typography>

        {/* Row 3: Address (full, with city bolded) */}
        <Tooltip title={address}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              fontSize: '0.75rem',
              lineHeight: 1.3,
              wordBreak: 'break-word',
            }}
          >
            {parsedAddress ? (
              <>
                {parsedAddress.beforeCity}
                <Box component="span" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                  {parsedAddress.city}
                </Box>
                {parsedAddress.afterCity}
              </>
            ) : (
              address
            )}
          </Typography>
        </Tooltip>

        {/* Row 4: Descartes ID or ShipStation ID (if exists) */}
        {row.descartesOrderId && (
          <Chip
            size="small"
            label={`D: ${row.descartesOrderId}`}
            color={row.descartesError ? 'error' : 'success'}
            sx={{ height: 16, fontSize: '0.65rem', alignSelf: 'flex-start', mt: 0.25 }}
          />
        )}
        {!row.descartesOrderId && row.shipstationOrderId && (
          <Chip
            size="small"
            label={`SS: ${row.shipstationOrderId}`}
            sx={{
              height: 16,
              fontSize: '0.65rem',
              alignSelf: 'flex-start',
              mt: 0.25,
              backgroundColor: stickySurface,
              color: 'text.primary',
              border: '1px solid',
              borderColor: 'divider',
            }}
          />
        )}
      </Stack>
    );
  }
});

// ============ DELIVERY STATUS QUEUE COMPACT COLUMNS ============
// Compact columns specific to the Delivery Status Queue (8-column layout)

// Column 1: Order Info with same-day/troubleshooting indicators (140px)
export const createDeliveryOrderInfoColumn = (): GridColDef => ({
  field: 'orderNumber',
  headerName: 'Order Info',
  width: 140,
  sortable: true,
  renderCell: (params: GridRenderCellParams) => {
    // A cancelled order is never "in troubleshooting" — guard against stale
    // flags so the TROUBLE chip can't linger after cancellation (UAT 2026-05-22).
    const isInTroubleshooting =
      params.row.inTroubleshooting && params.row.workflowStage !== 'cancelled';
    const isSameDayReschedule = params.row.isSameDayReschedule;

    return (
      <Stack spacing={0.25} sx={{ py: 0.5 }}>
        <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.85rem' }}>
          {params.row.orderNumber || params.row.id?.slice(0, 8)}
        </Typography>
        {/* Same-Day Reschedule Indicator */}
        {isSameDayReschedule && (
          <Tooltip title="Same-day reschedule - bypassed Ready to Ship">
            <Chip
              icon={<LocalShipping sx={{ fontSize: 12 }} />}
              label="SAME-DAY"
              color="warning"
              size="small"
              sx={{ height: 18, fontSize: '0.6rem', fontWeight: 'bold' }}
            />
          </Tooltip>
        )}
        {/* Troubleshooting Indicator */}
        {isInTroubleshooting && (
          <Tooltip title={`Troubleshooting: ${(params.row.troubleshootingReason || 'Unknown').replace(/_/g, ' ')}`}>
            <Chip
              icon={<Warning sx={{ fontSize: 12 }} />}
              label="TROUBLE"
              color="error"
              size="small"
              sx={{ height: 18, fontSize: '0.6rem', fontWeight: 'bold' }}
            />
          </Tooltip>
        )}
      </Stack>
    );
  },
});

// Column 4: Delivery date with Descartes stop/ETA (130px)
export const createDeliveryStatusDateColumn = (): GridColDef => ({
  field: 'deliveryDate',
  headerName: 'Delivery',
  width: 130,
  sortable: true,
  renderCell: (params: GridRenderCellParams) => {
    if (!params.row.deliveryDate) {
      return <Typography variant="body2" color="text.secondary">—</Typography>;
    }

    let dateStr = params.row.deliveryDate;
    if (typeof dateStr === 'string' && dateStr.includes('T')) {
      dateStr = dateStr.split('T')[0];
    }

    // Check if order is completed (should not show as overdue)
    const descartesStatus = params.row.metadata?.descartesDeliveryStatus;
    const isCompleted =
      params.row.workflowStage === 'delivered' ||
      params.row.workflowStage === 'delivery_missed' ||
      descartesStatus === '35' || // Descartes completed
      descartesStatus === '9' ||  // Descartes missed
      params.row.status === 'completed' ||
      params.row.status === 'delivered';

    // Calculate aging bucket color
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const deliveryDate = new Date(dateStr);
    deliveryDate.setHours(0, 0, 0, 0);
    const daysUntil = Math.ceil((deliveryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

    // Completed orders use green, pending orders use aging bucket color
    const color = isCompleted ? '#22c55e' : getAgingBucketColor(daysUntil);

    // Build Descartes stop/ETA chip label
    const stopNum = params.row.descartesStopNumber;
    const eta = params.row.descartesPlannedArrivalTime;
    let chipLabel = '';
    if (stopNum && eta) {
      chipLabel = `#${stopNum} • ${eta}`;
    } else if (stopNum) {
      chipLabel = `Stop #${stopNum}`;
    } else if (eta) {
      chipLabel = `Est: ${eta}`;
    }

    return (
      <Stack spacing={0.25} sx={{ py: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 'bold', color, fontSize: '0.85rem' }}>
          {formatDateOnly(dateStr)}
        </Typography>
        {chipLabel && (
          <Chip
            label={chipLabel}
            size="small"
            color="info"
            variant="outlined"
            sx={{ height: 18, fontSize: '0.65rem' }}
          />
        )}
      </Stack>
    );
  },
});

// Column 5: Status dropdown (pending/missed/delivered) - 130px
export interface DeliveryStatusColumnHandlers {
  handleStatusChange: (orderId: string, orderNumber: string, newStatus: string, oldStatus: string) => void;
  getShipmentStatus: (item: any) => 'pending' | 'delivered' | 'missed';
  getShipmentStatusLabel: (status: 'pending' | 'delivered' | 'missed') => string;
  getShipmentStatusColor: (status: 'pending' | 'delivered' | 'missed') => 'warning' | 'success' | 'error';
}

export const createDeliveryStatusColumn = (handlers: DeliveryStatusColumnHandlers): GridColDef => ({
  field: 'status',
  headerName: 'Status',
  width: 130,
  sortable: false,
  renderCell: (params: GridRenderCellParams) => {
    const shipmentStatus = handlers.getShipmentStatus(params.row);

    return (
      <FormControl size="small" fullWidth onClick={(e) => e.stopPropagation()}>
        <Select
          value={shipmentStatus}
          onChange={(e) => {
            e.stopPropagation();
            handlers.handleStatusChange(
              params.row.id,
              params.row.orderNumber || params.row.rxNumber,
              e.target.value,
              params.row.status
            );
          }}
          size="small"
          sx={{ minWidth: 100, fontSize: '0.8rem' }}
          renderValue={(value) => (
            <Chip
              label={handlers.getShipmentStatusLabel(value as any)}
              color={handlers.getShipmentStatusColor(value as any)}
              size="small"
              sx={{ height: 22, fontSize: '0.7rem' }}
            />
          )}
        >
          <MenuItem value="pending">Pending</MenuItem>
          <MenuItem value="missed">Missed</MenuItem>
          <MenuItem value="delivered">Completed</MenuItem>
        </Select>
      </FormControl>
    );
  },
});

// Column 6: Proof of Delivery info (delivered at, signature, driver) - 120px
export const createCompactPODColumn = (): GridColDef => ({
  field: 'deliveredAt',
  headerName: 'POD Info',
  width: 120,
  sortable: true,
  renderCell: (params: GridRenderCellParams) => {
    // Only show for delivered orders
    if (!params.row.deliveredAt) {
      return <Typography variant="caption" color="text.disabled">—</Typography>;
    }

    const hasSignature = params.row.hasSignature;
    const driver = params.row.completedByDriver;

    return (
      <Stack spacing={0.25} sx={{ py: 0.5 }}>
        <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
          {formatDateTime(params.row.deliveredAt)}
        </Typography>
        <Stack direction="row" spacing={0.5} alignItems="center">
          {hasSignature ? (
            <Tooltip title={params.row.signedBy ? `Signed by: ${params.row.signedBy}` : 'Signature captured'}>
              <Chip
                icon={<DrawIcon sx={{ fontSize: 10 }} />}
                label="Signed"
                color="success"
                size="small"
                sx={{ height: 16, fontSize: '0.6rem', '& .MuiChip-icon': { fontSize: 10 } }}
              />
            </Tooltip>
          ) : (
            <Chip
              label="No Sig"
              color="warning"
              size="small"
              sx={{ height: 16, fontSize: '0.6rem' }}
            />
          )}
        </Stack>
        {driver && (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
            {driver}
          </Typography>
        )}
      </Stack>
    );
  },
});

// Column 7: Stacked notes (order note + delivery instructions) - 150px
export interface DeliveryNotesColumnHandlers {
  setSelectedItemForNotes: (item: any) => void;
  setDeliveryInstructionsDialogOpen: (open: boolean) => void;
}

export const createDeliveryNotesColumn = (handlers: DeliveryNotesColumnHandlers): GridColDef => ({
  field: 'notes',
  headerName: 'Notes',
  width: 150,
  sortable: false,
  renderCell: (params: GridRenderCellParams) => {
    const orderNote = params.row.note || params.row.internalNote;
    const deliveryInstructions = params.row.specialInstructions;

    const hasAnyNote = orderNote || deliveryInstructions;

    if (!hasAnyNote) {
      return (
        <Typography variant="caption" color="text.disabled" sx={{ py: 0.5 }}>
          No notes
        </Typography>
      );
    }

    return (
      <Stack spacing={0.25} sx={{ py: 0.5, width: '100%' }}>
        {orderNote && (
          <Tooltip title={orderNote}>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <StickyNote2 sx={{ fontSize: 12, color: 'primary.main' }} />
              <Typography
                variant="caption"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: '0.7rem',
                  color: 'text.secondary',
                  maxWidth: 120,
                }}
              >
                {orderNote}
              </Typography>
            </Stack>
          </Tooltip>
        )}
        {deliveryInstructions && (
          <Tooltip title={deliveryInstructions}>
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              sx={{
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' },
                borderRadius: 0.5,
                px: 0.25,
              }}
              onClick={(e) => {
                e.stopPropagation();
                handlers.setSelectedItemForNotes(params.row);
                handlers.setDeliveryInstructionsDialogOpen(true);
              }}
            >
              <LocalShipping sx={{ fontSize: 12, color: 'warning.main' }} />
              <Typography
                variant="caption"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: '0.7rem',
                  color: 'warning.main',
                  fontWeight: 'medium',
                  maxWidth: 120,
                }}
              >
                {deliveryInstructions}
              </Typography>
            </Stack>
          </Tooltip>
        )}
      </Stack>
    );
  },
});

// Column 8: Simplified actions (View Order + Context Notes) - 100px
export interface DeliveryActionsColumnHandlers {
  handleViewOrder: (orderId: string) => void;
  setSelectedItemForContext: (item: any) => void;
  setContextNotesDrawerOpen: (open: boolean) => void;
}

export const createDeliveryActionsColumn = (handlers: DeliveryActionsColumnHandlers): GridColDef => ({
  field: 'actions',
  headerName: 'Actions',
  width: 100,
  sortable: false,
  cellClassName: 'actions-column',
  headerClassName: 'actions-column',
  renderCell: (params: GridRenderCellParams) => (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ py: 0.5 }}>
      <Tooltip title="View Order Details">
        <IconButton
          size="small"
          color="info"
          onClick={(e) => {
            e.stopPropagation();
            handlers.handleViewOrder(params.row.id);
          }}
          sx={{ p: 0.5 }}
        >
          <Description fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Context Notes">
        <IconButton
          size="small"
          color="info"
          onClick={(e) => {
            e.stopPropagation();
            handlers.setSelectedItemForContext(params.row);
            handlers.setContextNotesDrawerOpen(true);
          }}
          sx={{ p: 0.5 }}
        >
          <Info fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  ),
});

// ============ DELIVERY TYPE (READ-ONLY) COLUMN ============
// Simple read-only delivery type chip for queues where editing isn't needed

export const createReadOnlyDeliveryTypeColumn = (): GridColDef => ({
  field: 'deliveryType',
  headerName: 'Type',
  // GP-1347: room for "Nursing Home – <home>" (see createCompactDeliveryTypeColumn).
  width: 170,
  sortable: true,
  renderCell: (params: GridRenderCellParams) => {
    const orderType = params.row.orderType || 'Delivery';
    if (orderType === 'Pickup') {
      return <Chip label="Pickup" variant="outlined" size="small" sx={{ height: 22, fontSize: '0.7rem', opacity: 0.6 }} />;
    }
    // GP-1314: mirrors createCompactDeliveryTypeColumn — NH orders have no
    // courier delivery type at all, so they must not read as "Not Set".
    // GP-1347: same chip, so the home name reads identically in every queue.
    if (isNursingHomeOrder(params.row)) {
      return <NursingHomeDeliveryTypeChip row={params.row} />;
    }
    const deliveryType = params.row.deliveryType;
    const label = getDeliveryTypeLabel(deliveryType);
    const color = getDeliveryTypeColor(deliveryType);
    return (
      <Tooltip title={`Delivery Type: ${label}`}>
        <Chip label={label} color={color} size="small" sx={{ height: 22, fontSize: '0.7rem', fontWeight: 'medium' }} />
      </Tooltip>
    );
  },
});

// ============ TRACKING NUMBER COLUMN ============
// Displays tracking number as a clickable hyperlink to the carrier's tracking page

const CARRIER_TRACKING_URLS: Record<string, string> = {
  'canada_post': 'https://www.canadapost-postescanada.ca/track-reperage/en#/details/',
  'canadapost': 'https://www.canadapost-postescanada.ca/track-reperage/en#/details/',
  'ups': 'https://www.ups.com/track?tracknum=',
  'purolator': 'https://www.purolator.com/en/shipping/tracker?pin=',
};

// NKS Health FedEx shipper account — included in trkqual so FedEx opens the exact shipment
// rather than asking the user to disambiguate when the bare tracking number collides.
const FEDEX_SHIPPER_ACCOUNT = '2461173000';

export const getTrackingUrl = (carrierCode: string | null | undefined, trackingNumber: string): string | null => {
  if (!carrierCode || !trackingNumber) return null;
  const normalizedCarrier = carrierCode.toLowerCase().replace(/[-_\s]/g, '_');
  if (normalizedCarrier.includes('fedex')) {
    const encoded = encodeURIComponent(trackingNumber);
    return `https://www.fedex.com/fedextrack/?trknbr=${encoded}&trkqual=${FEDEX_SHIPPER_ACCOUNT}~${encoded}~FX`;
  }
  // Try exact match first, then prefix match
  const baseUrl = CARRIER_TRACKING_URLS[normalizedCarrier]
    || Object.entries(CARRIER_TRACKING_URLS).find(([key]) => normalizedCarrier.includes(key))?.[1];
  return baseUrl ? `${baseUrl}${encodeURIComponent(trackingNumber)}` : null;
};

export const createTrackingColumn = (): GridColDef => ({
  field: 'shipstationTrackingNumber',
  headerName: 'Tracking',
  width: 140,
  sortable: false,
  renderCell: (params: GridRenderCellParams) => {
    const trackingNumber = params.row.shipstationTrackingNumber;
    const carrierCode = params.row.shipstationCarrierCode;

    if (!trackingNumber) return <Typography variant="caption" color="text.secondary">—</Typography>;

    const trackingUrl = getTrackingUrl(carrierCode, trackingNumber);
    const carrierLabel = carrierCode ? carrierCode.replace(/_/g, ' ').toUpperCase() : '';

    return (
      <Stack spacing={0} sx={{ py: 0.5 }}>
        {trackingUrl ? (
          <Tooltip title={`Track on ${carrierLabel || 'carrier site'}`}>
            <Typography
              variant="caption"
              component="a"
              href={trackingUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: 'primary.main',
                textDecoration: 'underline',
                cursor: 'pointer',
                fontSize: '0.7rem',
                '&:hover': { color: 'primary.dark' },
              }}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              {trackingNumber}
            </Typography>
          </Tooltip>
        ) : (
          <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>{trackingNumber}</Typography>
        )}
        {carrierLabel && (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
            {carrierLabel}
          </Typography>
        )}
      </Stack>
    );
  },
});

// ============ ALERT REASON COLUMN ============
// Used by AlertStatusQueue — renders alertReason as a colored MUI Chip + icon,
// and surfaces followUpUpdateFailed as a small warning chip when true.

type AlertReason = 'COURIER_NO_DELIVERY_SIGNAL' | 'INHOUSE_NO_COMPLETE_WEBHOOK' | 'MISSED_UNRESOLVED' | 'LTOM_AWAITING_MISSISSAUGA' | 'AGING';

const ALERT_REASON_CONFIG: Record<AlertReason, {
  label: string;
  color: 'error' | 'warning' | 'secondary' | 'info' | 'default';
  icon: React.ReactElement | undefined;
}> = {
  COURIER_NO_DELIVERY_SIGNAL: {
    label: 'No Delivery Signal',
    color: 'error',
    icon: <SignalCellularConnectedNoInternet0Bar sx={{ fontSize: 14 }} />,
  },
  INHOUSE_NO_COMPLETE_WEBHOOK: {
    label: 'No Complete Webhook',
    color: 'warning',
    icon: <WarningAmber sx={{ fontSize: 14 }} />,
  },
  MISSED_UNRESOLVED: {
    label: 'Missed – Unresolved',
    color: 'secondary',
    icon: <ReportProblem sx={{ fontSize: 14 }} />,
  },
  LTOM_AWAITING_MISSISSAUGA: {
    label: 'LtoM–MM — awaiting Mississauga',
    color: 'info',
    icon: <Loop sx={{ fontSize: 14 }} />,
  },
  AGING: {
    label: 'Aging',
    color: 'default',
    icon: undefined,
  },
};

export const createAlertReasonColumn = (): GridColDef => ({
  field: 'alertReason',
  headerName: 'Alert Reason',
  width: 200,
  sortable: false,
  renderCell: (params: GridRenderCellParams) => {
    const reason = params.row.alertReason as AlertReason | undefined;
    const daysOverdue: number = params.row.daysOverdue ?? 0;
    const followUpUpdateFailed: boolean = params.row.followUpUpdateFailed ?? false;

    const config = reason ? ALERT_REASON_CONFIG[reason] : null;

    return (
      <Stack spacing={0.25} sx={{ py: 0.5 }}>
        {config ? (
          <Tooltip title={daysOverdue > 0 ? `${daysOverdue} day${daysOverdue !== 1 ? 's' : ''} overdue` : reason}>
            <Chip
              icon={config.icon ?? undefined}
              label={config.label}
              color={config.color}
              size="small"
              sx={{ height: 22, fontSize: '0.7rem', fontWeight: 'medium' }}
            />
          </Tooltip>
        ) : (
          <Typography variant="caption" color="text.secondary">—</Typography>
        )}
        {daysOverdue > 0 && (
          <Typography variant="caption" color="error.main" sx={{ fontSize: '0.65rem' }}>
            {daysOverdue}d overdue
          </Typography>
        )}
        {followUpUpdateFailed && (
          <Chip
            icon={<Warning sx={{ fontSize: 12 }} />}
            label="Follow-up failed"
            color="warning"
            size="small"
            variant="outlined"
            sx={{ height: 18, fontSize: '0.6rem' }}
          />
        )}
      </Stack>
    );
  },
});
